import { clamp, lerp, lerpAngle, wrapIndex } from './core/math';
import { IDLE_RPM, REDLINE_RPM, TICK_HZ } from './physics/constants';
import { speedOf, type KartState } from './physics/vehicle';
import { ACCESSIBLE_REALISM_MODES, realismById, type RealismProfile } from './physics/realism';
import { tyreLabel } from './physics/tyres';
import { REAL_CIRCUITS, circuitForKey, circuitKey } from './track/circuits';
import { generateTrack, GP_TRACK_WIDTH, TRACK_WIDTH, type LapLength } from './track/generate';
import { effectiveCurvature, solveSpeedProfile, topSpeed } from './track/speedprofile';
import { buildCorners, type Corner } from './track/brakingzones';
import {
  buildDrill,
  describeDrill,
  hasApproach,
  type DrillMode,
  type DrillResult,
  type DrillSegment,
} from './game/drill';
import { dateKey, fnv1a, msUntilNextTrack, seedForDate } from './track/rng';
import { buildWeather, rollWeather, type Weather } from './track/weather';
import { GameLoop } from './game/loop';
import { InputManager } from './game/input';
import { quantiseBrake } from './game/controls';
import { Session, ghostPose, GHOST_STRIDE, type Ghost } from './game/session';
import {
  buildGrandPrixStages,
  createGrandPrix,
  DEFAULT_GRID_SIZE,
  GRID_SIZES,
  grandPrixPointsTotal,
  grandPrixTotal,
  isLastGrandPrixStage,
  recordGrandPrixAttempt,
  recordGrandPrixTime,
  type GrandPrixRun,
  type GrandPrixStage,
  type GridSize,
} from './game/grandprix';
import {
  buildAIGrid,
  buildAIRoster,
  gridSlotPose,
  launchAIGrid,
  tickAIGrid,
  type AIRacer,
} from './game/airacer';
import {
  advanceSegment,
  applyPlayerLap,
  classification,
  createQuali,
  cutCount,
  gridRowsFor,
  isQualiComplete,
  microStripColors,
  orderedField,
  segmentExhausted,
  theoreticalBestMs,
  type QualiDriver,
  type QualiState,
} from './game/qualifying';
import { resolveKartCollisions } from './game/collision';
import { computeSlipstream } from './game/slipstream';
import {
  buildTelemetry,
  drawFrictionCircle,
  drawTraces,
  readoutAt,
  type TelemetryModel,
  type TelemetryReadout,
} from './render/telemetry';
import { buildReferenceGhost } from './game/replay';
import { Autopilot } from './game/autopilot';
import { AutoBrake } from './game/autobrake';
import { describeWorst, type CornerDelta } from './game/analysis';
import { MEDALS, medalFor, medalTime, nextMedal, isBetterMedal } from './game/medals';
import { evaluateObjectives, objectivesFor, type ObjectiveResult } from './game/objectives';
import { buildShareText } from './game/sharetext';
import { simulateTrace } from './game/replay';
import { base64ToBytes } from './storage/codec';
import { fetchRivalLap } from './storage/leaderboard';
import { mergeBestSplits, optimalLap, splitsOf } from './storage/local';
import { Effects, renderLiveryPreview } from './render/entities';
import type { RenderWorld, WorldFrame, WorldRenderer } from './render/renderer';
import { Canvas2DRenderer } from './render/renderer2d';
import { Camera } from './render/camera';
import { formatDelta, formatTime, Hud } from './render/hud';
import { LIVERIES, liveryById } from './render/liveries';
import { renderShareCard, shareCardBlob } from './render/sharecard';
import { GameAudio } from './audio/engine';
import { decodeGhost, encodeGhost } from './storage/codec';
import { loadDay, saveDay, type DayRecord } from './storage/local';
import {
  loadSettings,
  updateSettings,
  type Camera3dMode,
  type OverheadOrientation,
  type RendererMode,
  type Settings,
  type TelemetryLayer,
} from './storage/settings';
import { buildSubmission } from './storage/submission';
import {
  bestGapEver,
  currentStreak,
  gapPercent,
  loadStats,
  longestStreak,
  recordLap,
} from './storage/stats';
import {
  fetchLeaderboard,
  leaderboardEnabled,
  playerName,
  setPlayerName,
  submitRun,
  type LeaderboardPage,
} from './storage/leaderboard';
import {
  clearRoomCode,
  createRoom,
  fetchRoomStandings,
  joinRoom,
  roomCode,
  setRoomCode,
  submitRoomStage,
  type RoomStandingsPage,
} from './storage/room';
import type { RoomStandingRow } from './game/roomstandings';

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as T;
};

const canvas = $<HTMLCanvasElement>('stage');
const ctx = canvas.getContext('2d', { alpha: false });
if (!ctx) throw new Error('canvas 2d context unavailable');

const canvas3d = $<HTMLCanvasElement>('stage3d');
const hudCanvas = $<HTMLCanvasElement>('stage-hud');
const hudCtx = hudCanvas.getContext('2d');
if (!hudCtx) throw new Error('hud canvas 2d context unavailable');

function resizeHud(): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = hudCanvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));
  if (hudCanvas.width !== w * dpr || hudCanvas.height !== h * dpr) {
    hudCanvas.width = w * dpr;
    hudCanvas.height = h * dpr;
  }
}

let settings: Settings = loadSettings();

// ---------------------------------------------------------------- the world

interface World {
  key: string;
  label: string;
  seed: number;
  /** The circuit was asked for by seed or by track width rather than by date
   *  alone, so it is a circuit of its own: a rebuild has to reproduce it, and
   *  it can never become today's. */
  custom: boolean;
  isToday: boolean;
  track: ReturnType<typeof generateTrack>;
  weather: Weather;
  profile: ReturnType<typeof solveSpeedProfile>;
  corners: Corner[];
}

const todayKey = dateKey();
/** Computed once — it depends only on fixed physics constants, not the day's
 *  circuit, and the HUD needs it every frame for the RPM readout. */
const CAR_TOP_SPEED = topSpeed();

function buildWorld(
  key: string,
  opts: { seed?: number; label?: string; length?: LapLength; width?: number } = {},
): World {
  const length = opts.length ?? settings.lapLength;
  const seed = opts.seed ?? seedForDate(key);
  const track = generateTrack(key, seed, length, opts.width);
  const weather = buildWeather(track, seed, rollWeather(seed));
  const profile = solveSpeedProfile(track, weather.muAt);
  const corners = buildCorners(track, profile);
  // A Grand Prix stage reuses the day's seed but a wider track, and a named
  // circuit is not the day's layout at all, so — like a custom seed — neither
  // may ever be treated as "today"; that would corrupt the real daily best,
  // leaderboard entry and stats with a lap driven on different geometry. Lap
  // length is deliberately not part of this: it is the one circuit option a
  // player can switch back, and switching back has to land on the daily again.
  const custom =
    opts.seed !== undefined ||
    circuitForKey(key) !== null ||
    (opts.width !== undefined && opts.width !== TRACK_WIDTH);
  return {
    key,
    label: opts.label ?? key,
    seed,
    custom,
    isToday: key === todayKey && !custom && length === 'standard',
    track,
    weather,
    profile,
    corners,
  };
}

function renderWorldOf(w: World): RenderWorld {
  return { track: w.track, corners: w.corners, profile: w.profile, weather: w.weather };
}

let world = buildWorld(todayKey);
let hud = new Hud(world.track, world.corners);
let session = makeSession();

/**
 * Which surface is currently drawing the world. Two implementations —
 * Canvas2D (default, and the WebGL fallback) and Three.js — share one
 * interface so nothing else in this file branches on which is active.
 */
let renderer: WorldRenderer = new Canvas2DRenderer(canvas, ctx);
let rendererMode: RendererMode = '2d';
renderer.setWorld(renderWorldOf(world));

const effects = new Effects();
const input = new InputManager();
const audio = new GameAudio();

let day: DayRecord = loadDay(storageKey(), recordMode());
let bestGhost: Ghost | null = null;
let referenceGhost: Ghost | null = null;
let rivalGhost: Ghost | null = null;
let rivalName = '';
let bestSectors: number[] | null = null;
let dailyObjectives = objectivesFor(world.seed);
let lastObjectiveResults: ObjectiveResult[] = [];
/** Per-corner losses from the most recent lap, for ordering the drill list. */
let lastResultDeltas: CornerDelta[] = [];
/** Active Grand Prix chain, or null outside the mode. */
let gp: GrandPrixRun | null = null;

/** Cache for in-flight leaderboard fetch to avoid duplicate requests. */
let leaderboardCache: { key: string; realism: string; promise: Promise<LeaderboardPage> } | null =
  null;
/** AI-driven karts sharing the track this attempt. Empty outside Grand Prix. */
let aiRacers: AIRacer[] = [];
/** Active elimination-qualifying session, or null outside a quali Grand Prix stage. */
let quali: QualiState | null = null;
/** The AI field quali built for this stage — reused to grid the race. */
let qualiRoster: { racers: AIRacer[]; paces: number[] } | null = null;
let prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };

/** Custom seeds and past days get their own records so they cannot pollute today's. */
function storageKey(): string {
  return world.isToday ? world.key : `${world.key}#${world.seed}`;
}

function activeRealism(): RealismProfile {
  const base = realismById(settings.realism);
  if (settings.brakeAssistOverride === null && settings.stabilityOverride === null) return base;
  return {
    ...base,
    brakeAssist: settings.brakeAssistOverride ?? base.brakeAssist,
    stabilityAssist: settings.stabilityOverride ?? base.stabilityAssist,
  };
}

/**
 * Stability control's strength when the player switches it on themselves, in a
 * mode that ships without any. Never *below* the mode's own figure: a checkbox
 * that turns an assist on must not leave it weaker than leaving it alone did,
 * which is what a flat 0.6 used to do in Arcade.
 */
const STABILITY_ON = 0.6;

/**
 * The key today's records shard on.
 *
 * Assists change lap times, which is why a Sport best and an Arcade best
 * already cannot overwrite each other (see storage/local.ts). Automatic braking
 * changes them by the same argument, so it earns its own shard rather than
 * quietly rewriting a best that was set without it.
 */
function recordMode(realism: string = settings.realism): string {
  return settings.autoBrake ? `${realism}+autobrake` : realism;
}

/**
 * Brakes for the player when the setting is on. Rebuilt with the session, which
 * is itself rebuilt whenever the world changes — the two are always a pair.
 */
let autoBrake: AutoBrake | null = null;
let autoBrakeFor: Session | null = null;

function autoBraker(): AutoBrake {
  if (!autoBrake || autoBrakeFor !== session) {
    autoBrake = new AutoBrake(world.track, world.profile, session.query, world.weather);
    autoBrakeFor = session;
  }
  return autoBrake;
}

function makeSession(): Session {
  return new Session({
    track: world.track,
    profile: world.profile,
    corners: world.corners,
    weather: world.weather,
    realism: activeRealism(),
    tyreModel: settings.tyreModel,
  });
}

function decodeBest(): Ghost | null {
  if (!day.ghostB64 || day.ghostMs === null) return null;
  try {
    return decodeGhost(
      day.ghostB64,
      world.track.bounds.minX,
      world.track.bounds.minY,
      day.ghostMs,
      Math.max(
        world.track.bounds.maxX - world.track.bounds.minX,
        world.track.bounds.maxY - world.track.bounds.minY,
      ),
    );
  } catch {
    return null;
  }
}

function activeGhost(): Ghost | null {
  if (settings.ghost === 'none') return null;
  if (settings.ghost === 'reference') {
    referenceGhost ??= buildReferenceGhost({
      track: world.track,
      profile: world.profile,
      corners: world.corners,
      weather: world.weather,
      realism: activeRealism(),
    });
    return referenceGhost;
  }
  // Rival falls back to your own best until the fetch lands.
  if (settings.ghost === 'rival') return rivalGhost ?? bestGhost;
  return bestGhost;
}

/**
 * Pulls the lap of the player just ahead and re-simulates it locally.
 *
 * The server sends the input trace, not positions — the same bytes it uses to
 * verify the time — so a rival ghost is provably a lap that really happened
 * under identical physics, and it costs one small request.
 */
async function loadRivalGhost(): Promise<void> {
  rivalGhost = null;
  rivalName = '';
  if (settings.ghost !== 'rival' || !leaderboardEnabled() || !world.isToday) return;

  const rival = await fetchRivalLap(world.key, settings.realism);
  if (!rival) return;
  try {
    const replayed = simulateTrace(
      {
        track: world.track,
        profile: world.profile,
        corners: world.corners,
        weather: world.weather,
        realism: realismById(settings.realism),
      },
      base64ToBytes(rival.traceB64),
    );
    rivalGhost = replayed.result?.ghost ?? null;
    rivalName = rival.name;
    if (rivalGhost) {
      $('ghost-hint').textContent =
        `Chasing ${rivalName} — P${rival.rank}, ${formatTime(rival.timeMs)}.`;
    }
  } catch {
    rivalGhost = null;
  }
}

/** Rebuilds everything after a circuit, length or mode change. */
function reloadWorld(next: World): void {
  world = next;
  renderer.setWorld(renderWorldOf(world));
  hud = new Hud(world.track, world.corners);
  session = makeSession();
  // Any AI grid belongs to the track it was placed on; carrying it into a new
  // one would render karts floating over geometry they were never driven on.
  aiRacers = [];
  quali = null;
  qualiRoster = null;
  referenceGhost = null;
  rivalGhost = null;
  dailyObjectives = objectivesFor(world.seed);
  lastObjectiveResults = [];
  day = loadDay(storageKey(), recordMode());
  bestGhost = decodeBest();
  bestSectors = day.bestSectors ?? null;
  effects.clear();
  prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
  renderer.snap(session.kart.x, session.kart.y, session.kart.heading);
  populateRace();
  renderAttemptBars();
  void refreshLeaderboard();
  void loadRivalGhost();
}

// ------------------------------------------------------------------ panels

const startPanel = $('start-panel');
const resultPanel = $('result-panel');
const practicePanel = $('practice-panel');
const sharePanel = $('share-panel');
const grandprixPanel = $('grandprix-panel');
const grandprixResultPanel = $('grandprix-result-panel');
const qualiPanel = $('quali-panel');
const replayBar = $('replay-bar');
const telemetryPanel = $('telemetry-panel');
const drillHud = $('drill-hud');

function hidePanels(): void {
  startPanel.hidden = true;
  resultPanel.hidden = true;
  practicePanel.hidden = true;
  sharePanel.hidden = true;
  grandprixPanel.hidden = true;
  grandprixResultPanel.hidden = true;
  qualiPanel.hidden = true;
}

function showStart(): void {
  hidePanels();
  startPanel.hidden = false;
  syncHowTo();
  renderSetupSummary();
}

/**
 * The technique copy is open for a player who has never finished a lap and
 * collapsed for everyone else — explained once, rather than stacked above the
 * primary action on every visit forever. `totalLaps` already records exactly
 * this, so it needs no setting of its own.
 */
function syncHowTo(): void {
  $<HTMLDetailsElement>('howto').open = loadStats().totalLaps === 0;
}

/** Points to the settings for assists, tyres, ghost, graphics, audio, accessibility, account. */
function renderSetupSummary(): void {
  $('setup-summary-text').textContent = 'More settings';
}

function populateRace(): void {
  $('circuit-title').textContent = world.isToday ? "Today's circuit" : world.label;
  $('track-date').textContent = world.label;
  $('stat-length').textContent = `${Math.round(world.track.length)} m`;
  $('stat-corners').textContent = String(world.corners.length);
  $('stat-ideal').textContent = formatTime(world.profile.idealLapTime * 1000);
  $('weather-tag').textContent = world.weather.name;

  const note = $('weather-note');
  if (world.weather.id === 'dry') {
    note.hidden = true;
  } else {
    // The weather name is already in the top bar (P9) — the note carries only
    // the actionable blurb, which keeps it to one line on the Race fold budget.
    note.hidden = false;
    note.textContent = world.weather.blurb;
  }

  renderMedalTargets();
  renderObjectiveList($('objective-list'), null);
  renderGrandPrixStartBanner();
  void renderMenuLeaderboard();
}

/** The medal ladder for this circuit, with the one you have marked. */
function renderMedalTargets(): void {
  const host = $('medal-targets');
  host.replaceChildren();
  const idealMs = world.profile.idealLapTime * 1000;
  const earned = day.medal as Parameters<typeof isBetterMedal>[1];

  for (const medal of [...MEDALS].reverse()) {
    const el = document.createElement('div');
    const has = earned != null && !isBetterMedal(medal.id, earned);
    el.className = 'medal-target' + (has ? ' earned' : '');
    el.style.setProperty('--medal', medal.color);
    el.innerHTML =
      `<span class="medal-name">${medal.name}</span>` +
      `<strong>${formatTime(medalTime(idealMs, medal))}</strong>`;
    host.append(el);
  }
}

function renderObjectiveList(host: HTMLElement, results: ObjectiveResult[] | null): void {
  host.replaceChildren();
  for (let i = 0; i < dailyObjectives.length; i++) {
    const objective = dailyObjectives[i];
    const done = results ? results[i]?.done : day.objectives.includes(objective.id);
    const el = document.createElement('div');
    el.className = 'objective' + (done ? ' done' : '');
    el.innerHTML =
      `<span class="tick">${done ? '✓' : '○'}</span>` +
      `<span><strong>${objective.name}</strong><em>${objective.detail}</em></span>`;
    el.title = objective.detail;
    host.append(el);
  }
}

// ---------------------------------------------------------------- pickers

function buildChips(
  hostIds: string | string[],
  items: Array<{ id: string; label: string }>,
  selected: () => string,
  onPick: (id: string) => void,
): void {
  const hosts = Array.isArray(hostIds) ? hostIds : [hostIds];
  const lists = hosts.map((id) => $(id));

  for (const list of lists) {
    list.replaceChildren();
    // A lone option is a fixed choice, not a row to pick from — let it size to
    // its label instead of stretching across an empty grid.
    list.classList.toggle('solo', items.length === 1);
    for (const item of items) {
      const chip = document.createElement('button');
      chip.className = 'mode-chip';
      chip.type = 'button';
      chip.dataset.id = item.id;
      chip.textContent = item.label;
      chip.setAttribute('aria-pressed', String(item.id === selected()));
      chip.addEventListener('click', () => {
        onPick(item.id);
        for (const hostList of lists) {
          for (const other of hostList.querySelectorAll('.mode-chip')) {
            other.setAttribute('aria-pressed', String(other.getAttribute('data-id') === selected()));
          }
        }
      });
      list.append(chip);
    }
  }
}

function applyMode(id: string): void {
  // Picking a mode is the reset. It hands back that mode's own assists, which
  // is exactly what the picker's blurb promises; an assist the player changes
  // afterwards holds until they pick a mode again.
  settings = updateSettings({
    realism: id as Settings['realism'],
    brakeAssistOverride: null,
    stabilityOverride: null,
  });
  session.realism = activeRealism();
  syncToggles();

  const mode = realismById(id);
  $('mode-tag').textContent = mode.name;
  $('mode-blurb').textContent = mode.blurb;
  const detail = $('mode-detail-list');
  detail.replaceChildren();
  for (const line of mode.detail) {
    const li = document.createElement('li');
    li.textContent = line;
    detail.append(li);
  }

  day = loadDay(storageKey(), recordMode(id));
  bestGhost = decodeBest();
  bestSectors = day.bestSectors ?? null;
  renderAttemptBars();
  renderSetupSummary();
  void refreshLeaderboard();
}

function buildLiveryChips(hostIds: string[]): void {
  const lists = hostIds.map((id) => $(id));

  for (const list of lists) {
    list.replaceChildren();
    for (const l of LIVERIES) {
      const chip = document.createElement('button');
      chip.className = 'livery-chip';
      chip.type = 'button';
      // The sync below matches on `data-id`, same as buildChips does — without
      // it every chip in every host read unpressed after a pick.
      chip.dataset.id = l.id;
      chip.setAttribute('aria-pressed', String(l.id === settings.livery));
      const preview = document.createElement('canvas');
      preview.className = 'livery-preview';
      preview.setAttribute('aria-hidden', 'true');
      renderLiveryPreview(preview, l);
      chip.append(preview, document.createTextNode(l.name));
      chip.addEventListener('click', () => {
        settings = updateSettings({ livery: l.id });
        for (const hostList of lists) {
          for (const other of hostList.querySelectorAll('.livery-chip')) {
            other.setAttribute('aria-pressed', String(other.getAttribute('data-id') === l.id));
          }
        }
        renderSetupSummary();
      });
      list.append(chip);
    }
  }
}

function buildPickers(): void {
  buildChips(
    ['mode-list', 'mode-list-race'],
    ACCESSIBLE_REALISM_MODES.map((m) => ({ id: m.id, label: m.name })),
    () => settings.realism,
    applyMode,
  );

  buildChips(
    'ghost-list',
    [
      { id: 'best', label: 'Your best' },
      { id: 'reference', label: 'Reference' },
      { id: 'rival', label: 'Rival' },
      { id: 'none', label: 'None' },
    ],
    () => settings.ghost,
    (id) => {
      settings = updateSettings({ ghost: id as Settings['ghost'] });
      void loadRivalGhost();
    },
  );

  buildChips(
    'length-list',
    [
      { id: 'standard', label: 'Standard ~40s' },
      { id: 'short', label: 'Short ~20s' },
    ],
    () => settings.lapLength,
    (id) => {
      settings = updateSettings({ lapLength: id as LapLength });
      // Only a custom circuit's seed survives the rebuild. Carrying the seed
      // for any world that merely wasn't today's meant a short lap pinned the
      // player off the daily forever — laps stopped counting towards stats,
      // streak and the leaderboard with nothing on screen to say why.
      reloadWorld(buildWorld(world.key, {
        seed: world.custom ? world.seed : undefined,
        label: world.label,
      }));
    },
  );

  buildLiveryChips(['livery-list', 'livery-list-race']);
}

// ---------------------------------------------------------------- options

/**
 * Re-reads every bound toggle from settings.
 *
 * Needed because a checkbox is not always the only thing that moves its
 * setting: the two assist boxes show the *effective* assist rather than the
 * stored override, and picking a realism mode resets those overrides, so both
 * boxes have to follow the mode picker.
 */
const toggleSyncs: Array<() => void> = [];

function syncToggles(): void {
  for (const sync of toggleSyncs) sync();
}

function bindToggle(id: string, get: () => boolean, set: (v: boolean) => void): void {
  const el = $<HTMLInputElement>(id);
  const sync = (): void => {
    el.checked = get();
  };
  sync();
  toggleSyncs.push(sync);
  el.addEventListener('change', () => set(el.checked));
}

function applyTextScale(): void {
  document.documentElement.style.fontSize = `${(settings.textScale / 100) * 16}px`;
}

/**
 * Mirrors the two accessibility settings onto the root element so the
 * stylesheet can answer them itself — `:root[data-colorblind='true']` swaps the
 * pace tokens to the same blue/yellow the track renderer uses, and
 * `:root[data-reduced-motion='true']` turns every transition and animation off.
 * Neither setting had any effect on the DOM before this.
 */
function applyAccessibilityFlags(): void {
  const root = document.documentElement;
  root.dataset.colorblind = String(settings.colorblind);
  root.dataset.reducedMotion = String(settings.reducedMotion);
  applyOverheadOrientation();
}

/**
 * Pushes the effective overhead orientation to whichever renderer is active.
 * The 2D top-down view is always kart-up; only the 3D "Overhead" camera honours
 * the `overheadOrientation` setting. Reduced motion always wins: a continuously
 * rotating world is a motion trigger, so it is held back to `track-up`.
 */
function effectiveOverheadOrientation(
  mode: RendererMode = settings.renderer,
): OverheadOrientation {
  if (settings.reducedMotion) return 'track-up';
  if (mode === '2d') return 'kart-up';
  return settings.overheadOrientation;
}

function applyOverheadOrientation(): void {
  renderer.setOverheadOrientation(effectiveOverheadOrientation());
}

// ------------------------------------------------------------- 3D renderer

/**
 * Swaps the active world renderer. Three.js is only imported the moment it is
 * actually needed, so a player who never turns on 3D never downloads it.
 *
 * Falls back to 2D — and corrects the setting and the picker to match — if
 * WebGL is unavailable, so a bad 3D attempt can never leave the game unable
 * to start.
 */
async function switchRenderer(requested: RendererMode): Promise<void> {
  if (requested === rendererMode) return;

  let next: WorldRenderer;
  let landed: RendererMode = requested;
  if (requested === '3d') {
    try {
      const { ThreeWorldRenderer } = await import('./render/three/scene');
      next = new ThreeWorldRenderer(canvas3d);
    } catch (err) {
      console.warn('3D renderer unavailable, staying on 2D.', err);
      next = new Canvas2DRenderer(canvas, ctx!);
      landed = '2d';
    }
  } else {
    next = new Canvas2DRenderer(canvas, ctx!);
  }

  next.setWorld(renderWorldOf(world));
  next.setPalette(settings.colorblind);
  next.setCameraMode(settings.camera3d);
  next.setOverheadOrientation(effectiveOverheadOrientation(landed));
  next.snap(session.kart.x, session.kart.y, session.kart.heading);

  renderer.dispose();
  renderer = next;
  rendererMode = landed;
  canvas.hidden = landed !== '2d';
  canvas3d.hidden = landed !== '3d';

  settings = updateSettings({ renderer: landed });
  buildRendererPickers();
}

function buildRendererPickers(): void {
  buildChips(
    'renderer-list',
    [
      { id: '2d', label: 'Top-down' },
      { id: '3d', label: '3D' },
    ],
    () => settings.renderer,
    (id) => void switchRenderer(id as RendererMode),
  );

  $('camera3d-block').hidden = settings.renderer !== '3d';
  buildChips(
    'camera3d-list',
    [
      { id: 'chase', label: 'Chase' },
      { id: 'follow', label: 'Follow' },
      { id: 'overhead', label: 'Overhead' },
      { id: 'cockpit', label: '1st person' },
    ],
    () => settings.camera3d,
    (id) => {
      settings = updateSettings({ camera3d: id as Camera3dMode });
      renderer.setCameraMode(settings.camera3d);
      buildRendererPickers();
    },
  );

  // The 3D "Overhead" camera can either stay north-up or turn with the kart.
  // The 2D top-down view is always kart-up, so it gets no picker.
  const overheadActive =
    settings.renderer === '3d' && settings.camera3d === 'overhead';
  $('overhead-orient-block').hidden = !overheadActive;
  buildChips(
    'overhead-orient-list',
    [
      { id: 'track-up', label: 'Track-up' },
      { id: 'kart-up', label: 'Kart-up' },
    ],
    () => settings.overheadOrientation,
    (id) => {
      settings = updateSettings({ overheadOrientation: id as OverheadOrientation });
      applyOverheadOrientation();
    },
  );
}

function bindOptions(): void {
  bindToggle('opt-refline', () => settings.referenceLine, (v) => {
    settings = updateSettings({ referenceLine: v });
  });
  bindToggle('opt-colorblind', () => settings.colorblind, (v) => {
    settings = updateSettings({ colorblind: v });
    renderer.setPalette(v);
    applyAccessibilityFlags();
  });
  bindToggle('opt-motion', () => settings.reducedMotion, (v) => {
    settings = updateSettings({ reducedMotion: v });
    applyAccessibilityFlags();
  });

  buildRendererPickers();
  bindToggle('opt-sound', () => settings.sound, (v) => {
    settings = updateSettings({ sound: v });
    audio.setEnabled(v);
    if (v) audio.start();
  });
  bindToggle('opt-tyres', () => settings.tyreModel, (v) => {
    settings = updateSettings({ tyreModel: v });
    session.tyreModel = v;
  });
  // Both assist boxes show the *effective* state — what the kart will actually
  // do — and write an explicit value, so unticking one genuinely turns it off
  // rather than falling back to a mode default that may well have it on.
  // Picking a realism mode is what clears them back to that mode's own answer.
  bindToggle('opt-brakeassist', () => activeRealism().brakeAssist, (v) => {
    settings = updateSettings({ brakeAssistOverride: v });
    session.realism = activeRealism();
  });
  bindToggle('opt-stability', () => activeRealism().stabilityAssist > 0, (v) => {
    const modeLevel = realismById(settings.realism).stabilityAssist;
    settings = updateSettings({ stabilityOverride: v ? Math.max(modeLevel, STABILITY_ON) : 0 });
    session.realism = activeRealism();
  });
  bindToggle('opt-autobrake', () => settings.autoBrake, (v) => {
    settings = updateSettings({ autoBrake: v });
    // Switching it moves which shard today's records live in, so reload them —
    // the same refresh picking a realism mode does, and for the same reason.
    day = loadDay(storageKey(), recordMode());
    bestGhost = decodeBest();
    bestSectors = day.bestSectors ?? null;
    renderAttemptBars();
  });

  bindToggle('opt-brakecue', () => settings.brakeCue, (v) => {
    settings = updateSettings({ brakeCue: v });
  });

  const text = $<HTMLInputElement>('opt-textsize');
  text.value = String(settings.textScale);
  text.addEventListener('input', () => {
    settings = updateSettings({ textScale: Number(text.value) });
    applyTextScale();
  });

  const vol = $<HTMLInputElement>('opt-volume');
  vol.value = String(Math.round(settings.volume * 100));
  vol.addEventListener('input', () => {
    settings = updateSettings({ volume: Number(vol.value) / 100 });
    audio.setVolume(settings.volume);
  });

  const name = $<HTMLInputElement>('opt-name');
  name.value = playerName();
  name.addEventListener('change', () => setPlayerName(name.value));

  $('leaderboard-block').hidden = !leaderboardEnabled();
}

// ------------------------------------------------------------------- tabs

/**
 * The three menu columns. They are one tab body, not three: on a wide viewport
 * they render side by side and the tab bar hides their buttons, on a narrow one
 * `data-col` on the panel shows exactly one and the buttons select it. Both
 * layouts are pure CSS — this only records which column is current.
 */
const COLUMN_TABS = ['circuit', 'race', 'garage'];

/** Matches the breakpoint the three-column grid turns on at in styles.css. */
const wideMenu = window.matchMedia('(min-width: 1040px)');

let currentTab = 'race';

function selectTab(name: string): void {
  // Every column shares one body, so a column tab selects `race` and differs
  // only in which column `data-col` reveals.
  const isColumn = COLUMN_TABS.includes(name);
  const body = isColumn ? 'race' : name;
  currentTab = name;
  startPanel.dataset.col = isColumn ? name : 'race';

  const tabs = [...startPanel.querySelectorAll<HTMLElement>('.tab')];
  const bodies = [...startPanel.querySelectorAll<HTMLElement>('.tab-body')];
  for (const t of tabs) t.setAttribute('aria-selected', String(t.dataset.tab === name));
  for (const b of bodies) b.hidden = b.dataset.body !== body;
  if (name === 'stats') renderStats();
  startPanel.scrollTop = 0;
}

function bindTabs(): void {
  for (const tab of startPanel.querySelectorAll<HTMLElement>('.tab')) {
    tab.addEventListener('click', () => selectTab(tab.dataset.tab ?? 'race'));
  }
  // Widening the window shows all three columns at once and hides the Circuit
  // and Garage buttons, so a selection of either would leave no tab lit.
  wideMenu.addEventListener('change', () => {
    if (wideMenu.matches && COLUMN_TABS.includes(currentTab) && currentTab !== 'race') {
      selectTab('race');
    }
  });
}

// ---------------------------------------------------------------- circuit

function bindCircuit(): void {
  const picker = $<HTMLInputElement>('date-picker');
  picker.max = todayKey;
  picker.value = world.key;

  $('load-date').addEventListener('click', () => {
    const key = picker.value;
    if (!key) return;
    reloadWorld(buildWorld(key));
    showStart();
  });

  $('load-seed').addEventListener('click', () => {
    const raw = $<HTMLInputElement>('seed-input').value.trim();
    if (!raw) return;
    // Any string becomes a circuit; sharing the string shares the track.
    reloadWorld(buildWorld(todayKey, { seed: fnv1a(`custom:${raw}`), label: `“${raw}”` }));
    showStart();
  });

  $('back-to-today').addEventListener('click', () => {
    reloadWorld(buildWorld(todayKey));
    showStart();
  });

  // Real circuits. Each is its own fixed layout at its own length, so it is
  // loaded by key alone — there is no seed and no length to carry into it.
  const circuits = $('circuit-list');
  circuits.replaceChildren();
  for (const c of REAL_CIRCUITS) {
    const btn = document.createElement('button');
    btn.className = 'circuit-chip';
    btn.type = 'button';
    btn.title = `${c.fullName}, ${c.country} — ${c.blurb}`;
    const name = document.createElement('strong');
    name.textContent = c.name;
    const detail = document.createElement('span');
    detail.textContent = `${(c.length / 1000).toFixed(1)} km`;
    btn.append(name, detail);
    btn.addEventListener('click', () => {
      reloadWorld(buildWorld(circuitKey(c.id), { label: c.name }));
      showStart();
    });
    circuits.append(btn);
  }

  // Quick links to the last week.
  const recent = $('recent-days');
  recent.replaceChildren();
  for (let d = 1; d <= 7; d++) {
    const key = new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
    const btn = document.createElement('button');
    btn.className = 'day-chip';
    btn.type = 'button';
    btn.textContent = key.slice(5);
    btn.title = key;
    btn.addEventListener('click', () => {
      reloadWorld(buildWorld(key));
      showStart();
    });
    recent.append(btn);
  }
}

// ------------------------------------------------------------------ stats

/**
 * Shows or hides a plot together with the `.label` heading that introduces it,
 * so an empty profile gets the one "finish a lap" hint rather than four orphan
 * headings above blank boxes.
 *
 * The `hidden` property alone is not enough: `.label` and `.medal-tally` set
 * their own `display`, and an author rule beats the UA `[hidden]` rule whatever
 * the source order — the trap `styles.css` already documents for `#stage` and
 * `.gp-banner`. The attribute still goes on for assistive tech; the inline
 * display is what removes the box.
 */
function showPlot(el: HTMLElement, show: boolean): void {
  const label = el.previousElementSibling;
  const heading = label instanceof HTMLElement && label.classList.contains('label') ? label : null;
  for (const node of heading ? [heading, el] : [el]) {
    node.hidden = !show;
    node.style.display = show ? '' : 'none';
  }
}

/**
 * Sizes a chart's backing store to the box the stylesheet actually gives it,
 * times the display density, and scales the context so the drawing code below
 * keeps working in CSS pixels. The markup's fixed `width`/`height` attributes
 * are a different aspect ratio than that box, which stretched every plot
 * vertically and left it soft on a retina screen. Measuring on each draw is
 * what makes it survive a viewport or text-size change.
 *
 * Null while the tab body is hidden, where the box measures zero.
 */
function fitChart(
  el: HTMLCanvasElement,
): { c: CanvasRenderingContext2D; w: number; h: number } | null {
  const c = el.getContext('2d');
  if (!c) return null;
  const rect = el.getBoundingClientRect();
  const w = Math.round(rect.width);
  const h = Math.round(rect.height);
  if (w === 0 || h === 0) return null;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  if (el.width !== w * dpr || el.height !== h * dpr) {
    el.width = w * dpr;
    el.height = h * dpr;
  }
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { c, w, h };
}

function renderStats(): void {
  const stats = loadStats();
  $('stat-streak').textContent = String(currentStreak(stats, todayKey));
  $('stat-longest').textContent = String(longestStreak(stats));
  $('stat-days').textContent = String(stats.days.length);
  $('stat-laps').textContent = String(stats.totalLaps);

  const best = bestGapEver(stats);
  $('stat-bestgap').textContent = best ? `+${gapPercent(best).toFixed(1)}% · ${best.key}` : '—';

  const chart = $<HTMLCanvasElement>('stats-chart');
  const hasData = stats.days.length > 0;
  $('stats-empty').hidden = hasData;
  // Both directions matter — this runs on every visit to the tab, so the plots
  // have to come back the moment the first lap lands.
  showPlot($('medal-tally'), hasData);
  showPlot(chart, hasData);
  showPlot($('stats-histogram'), hasData);
  if (!hasData) return;
  renderMedalTally(stats);

  const pts = stats.days.slice(-60).map(gapPercent);
  renderHistogram(pts);

  // Sized after the plots are shown, or the boxes would still measure zero.
  const surface = fitChart(chart);
  if (!surface) return;
  const c = surface.c;
  c.clearRect(0, 0, surface.w, surface.h);

  const maxGap = Math.max(...pts, 5);
  const pad = 26;
  const w = surface.w - pad * 2;
  const h = surface.h - pad * 2;
  const x = (i: number): number => pad + (pts.length === 1 ? w / 2 : (i / (pts.length - 1)) * w);
  // Zero gap sits on the baseline, the worst day at the top of the plot.
  const y = (v: number): number => pad + h - (v / maxGap) * h;

  // Gridlines
  c.strokeStyle = 'rgba(255,255,255,0.08)';
  c.lineWidth = 1;
  for (let g = 0; g <= 4; g++) {
    const gy = pad + (g / 4) * h;
    c.beginPath();
    c.moveTo(pad, gy);
    c.lineTo(pad + w, gy);
    c.stroke();
  }

  c.strokeStyle = '#ff8a3d';
  c.lineWidth = 2.5;
  c.lineJoin = 'round';
  c.beginPath();
  pts.forEach((v, i) => (i === 0 ? c.moveTo(x(i), y(v)) : c.lineTo(x(i), y(v))));
  c.stroke();

  c.fillStyle = '#ff8a3d';
  pts.forEach((v, i) => {
    c.beginPath();
    c.arc(x(i), y(v), 3, 0, Math.PI * 2);
    c.fill();
  });

  c.fillStyle = '#8b95a5';
  c.font = '600 12px ui-monospace, Menlo, monospace';
  c.fillText(`+${maxGap.toFixed(0)}%`, 4, pad + 4);
  c.fillText('0%', 4, pad + h + 4);
}

/** How many of each medal you have collected across every day played. */
function renderMedalTally(stats: ReturnType<typeof loadStats>): void {
  const host = $('medal-tally');
  host.replaceChildren();
  const counts = new Map<string, number>();
  for (const d of stats.days) {
    const medal = medalFor(d.bestMs, d.idealMs);
    if (medal.id !== 'none') counts.set(medal.id, (counts.get(medal.id) ?? 0) + 1);
  }
  for (const medal of MEDALS) {
    const el = document.createElement('div');
    el.className = 'tally';
    el.style.setProperty('--medal', medal.color);
    el.innerHTML =
      `<strong>${counts.get(medal.id) ?? 0}</strong><span>${medal.name}</span>`;
    host.append(el);
  }
}

/**
 * Distribution of how close you get, bucketed.
 *
 * A line chart shows the trend; this shows the shape. Seeing the mass of your
 * results shift left over weeks is a different and more satisfying signal than
 * watching a single number wobble.
 */
function renderHistogram(gaps: number[]): void {
  const surface = fitChart($<HTMLCanvasElement>('stats-histogram'));
  if (!surface) return;
  const c = surface.c;
  c.clearRect(0, 0, surface.w, surface.h);
  if (gaps.length === 0) return;

  const buckets = [5, 10, 15, 20, 30, 45, Infinity];
  const labels = ['<5%', '5–10', '10–15', '15–20', '20–30', '30–45', '45%+'];
  const counts = new Array(buckets.length).fill(0);
  for (const g of gaps) counts[buckets.findIndex((b) => g < b)]++;

  const maxCount = Math.max(...counts, 1);
  const pad = 26;
  const w = surface.w - pad * 2;
  const h = surface.h - pad * 2;
  const barW = w / counts.length;

  c.font = '600 13px ui-monospace, Menlo, monospace';
  c.textAlign = 'center';
  counts.forEach((n, i) => {
    const bh = (n / maxCount) * h;
    const x = pad + i * barW;
    // Closer to the ideal is greener, so the good end of the chart looks good.
    c.fillStyle = `hsl(${140 - (i / counts.length) * 130}, 62%, 52%)`;
    c.fillRect(x + barW * 0.16, pad + h - bh, barW * 0.68, bh);

    c.fillStyle = '#8b95a5';
    c.fillText(labels[i], x + barW / 2, pad + h + 16);
    if (n > 0) {
      c.fillStyle = '#eef1f5';
      c.fillText(String(n), x + barW / 2, pad + h - bh - 6);
    }
  });
  c.textAlign = 'left';
}

// --------------------------------------------------------------- practice

/** Time lost at each corner on the best lap, for ordering the drill list. */
function cornerLosses(): Map<number, number> {
  const losses = new Map<number, number>();
  for (const d of lastResultDeltas) losses.set(d.number, d.lost);
  return losses;
}

function buildPracticeList(): void {
  const list = $('practice-list');
  list.replaceChildren();

  // Worst corner first. The whole point is to work on what is costing you, and
  // the results screen already knows what that is.
  const losses = cornerLosses();
  const ordered = [...world.corners].sort(
    (a, b) => (losses.get(b.number) ?? -1) - (losses.get(a.number) ?? -1),
  );

  for (const corner of ordered) {
    const btn = document.createElement('button');
    btn.className = 'practice-item';
    btn.type = 'button';
    // `direction` is the sign of centerline curvature in math convention
    // (CCW positive). The 2D camera applies no y-flip (see Camera.applyTransform),
    // so world +y renders straight down the screen — a CCW (direction === 1) path
    // is a right-hander as the driver actually sees it, not a left.
    const dir = corner.direction === 1 ? 'right' : 'left';
    const lost = losses.get(corner.number);
    // The radius shown has to match the apex speed shown next to it. vApex
    // comes from the width-aware profile — the wider effective radius a racing
    // line permits — so displaying the tighter raw centerline radius here
    // implies a grip figure well beyond what the kart can produce.
    const kappa = world.track.curvature[corner.apexIndex];
    const effRadius = 1 / effectiveCurvature(kappa, world.track.width);
    const radiusLabel = Number.isFinite(effRadius) ? `R${effRadius.toFixed(0)}m` : 'straight';
    btn.innerHTML =
      `<strong>T${corner.number}</strong>` +
      `<span>${dir} · ${radiusLabel}</span>` +
      `<span>${Math.round(corner.vApex * 3.6)} km/h apex</span>` +
      `<span class="zone">${lost !== undefined && lost > 0.01 ? `−${lost.toFixed(2)}s` : `${corner.brakeLength.toFixed(0)}m zone`}</span>`;
    btn.addEventListener('click', () => openDrill(corner));
    list.append(btn);
  }
}

// ------------------------------------------------------------------- drill

let drillCorner: Corner | null = null;
let drillMode: DrillMode = 'corner';
let drillGhost: Ghost | null = null;
/** Result of the attempt just finished, shown briefly over the track. */
let drillFlash: { text: string; sub: string; good: boolean; until: number } | null = null;
/** Result of the last finished attempt — kept past the flash so the HUD's
 *  Last/Note stats can compare consecutive tries, not just the newest one. */
let lastDrillResult: DrillResult | null = null;

/** Grand Prix start lights: when the "GO!" flash should stop showing, and
 *  whether the previous frame was still in the pre-race hold (edge-detects
 *  the exact moment the lights go out). */
let gpGoFlashUntil = 0;
let wasInGpHold = false;

function openDrill(corner: Corner): void {
  drillCorner = corner;
  drillMode = settings.drillApproach && hasApproach(world.track, world.corners, corner)
    ? 'approach'
    : 'corner';
  drillGhost = null;
  drillFlash = null;
  lastDrillResult = null;
  session.reset();
  // A drill is solo practice, in or out of a Grand Prix — AI racing the rest
  // of the circuit while the player loops one corner would be nonsensical.
  aiRacers = [];
  restartDrill();
}

/** (Re)starts the current drill. The loop calls this between every attempt. */
function restartDrill(): void {
  if (!drillCorner) return;
  const segment = buildDrill(world.track, world.profile, world.corners, drillCorner, drillMode);

  hidePanels();
  effects.clear();
  session.startDrill(segment);
  prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
  renderer.snap(session.kart.x, session.kart.y, session.kart.heading);
  lastCuedCorner = -1;
  audio.start();

  // The reference driver over just this segment, built once per drill so there
  // is something to chase rather than only a number.
  drillGhost ??= buildSegmentGhost(segment);
  updateDrillChrome(segment);
}

/**
 * Runs the reference driver across the drill segment.
 *
 * Reuses the autopilot and the centerline profile exactly as the lap-wide
 * reference ghost does; only the start and stop conditions differ.
 */
function buildSegmentGhost(segment: DrillSegment): Ghost | null {
  const probe = new Session({
    track: world.track,
    profile: world.profile,
    corners: world.corners,
    weather: world.weather,
    realism: activeRealism(),
  });
  const centerline = solveSpeedProfile(world.track, world.weather.muAt, 0);
  const pilot = new Autopilot(world.track, centerline, probe.query, 0.72);

  probe.startDrill(segment, 0);
  for (let i = 0; i < TICK_HZ * 60 && probe.phase === 'running'; i++) {
    const { steerByte, brakeByte } = pilot.sample(probe.kart);
    if (probe.tick(steerByte, brakeByte)) break;
  }
  return probe.snapshotGhost();
}

function exitDrill(): void {
  drillCorner = null;
  drillGhost = null;
  drillFlash = null;
  lastDrillResult = null;
  session.reset();
  buildPracticeList();
  hidePanels();
  practicePanel.hidden = false;
}

function updateDrillChrome(segment: DrillSegment): void {
  $('drill-label').textContent = segment.label;
  $('drill-ideal').textContent = formatTime(segment.idealMs);
  $('drill-best').textContent =
    session.drillBestMs === null ? '—' : formatTime(session.drillBestMs);
  $('drill-attempts').textContent = String(session.drillAttempts);

  const toggle = $<HTMLButtonElement>('drill-approach');
  const available = drillCorner
    ? hasApproach(world.track, world.corners, drillCorner)
    : false;
  toggle.hidden = !available;
  toggle.setAttribute('aria-pressed', String(segment.mode === 'approach'));
  toggle.textContent = segment.mode === 'approach' ? 'With approach' : 'Corner only';

  // Last/Note stay hidden until one attempt exists, then persist across
  // retries — the same "surface it instead of letting it flash and vanish"
  // treatment the replay panel's Worst-moment stat gets from worstFrame.
  const lastStat = $('drill-last-stat');
  const noteStat = $('drill-note-stat');
  if (lastDrillResult) {
    const delta = lastDrillResult.timeMs - lastDrillResult.idealMs;
    const lastValue = $('drill-last');
    lastValue.textContent = formatDelta(delta);
    lastValue.className = delta > 50 ? 'bad' : 'good';
    lastStat.hidden = false;

    const note = describeDrill(lastDrillResult);
    const noteValue = $('drill-note');
    noteValue.textContent = note;
    noteValue.className = note === 'clean' ? 'good' : 'bad';
    noteStat.hidden = false;
  } else {
    lastStat.hidden = true;
    noteStat.hidden = true;
  }
}

/** Called on the tick a drill attempt completes. Flashes, then loops. */
function onDrillComplete(): void {
  const r = session.drillResult;
  const segment = session.drill;
  if (!r || !segment) return;
  lastDrillResult = r;

  const delta = (r.timeMs - r.idealMs) / 1000;
  drillFlash = {
    text: formatTime(r.timeMs),
    sub: r.personalBest
      ? `best · +${delta.toFixed(2)} off ideal`
      : `+${delta.toFixed(2)} off ideal · ${describeDrill(r)}`,
    good: r.personalBest,
    until: performance.now() + 1200,
  };
  audio.blip(r.personalBest ? 880 : 440, 0.1);

  if (drillCorner) {
    const key = `${drillCorner.number}:${drillMode}`;
    const previous = day.cornerBests?.[key];
    if (previous === undefined || r.timeMs < previous) {
      day.cornerBests = { ...(day.cornerBests ?? {}), [key]: r.timeMs };
      saveDay(day, recordMode());
    }
  }

  // Straight back to the approach. Attempts per minute is the entire value of
  // drilling, so nothing — no panel, no keypress — sits between them.
  restartDrill();
}

// ----------------------------------------------------------------- replay

interface ReplayState {
  ghost: Ghost;
  model: TelemetryModel;
  timeMs: number;
  playing: boolean;
  rate: number;
}
let replay: ReplayState | null = null;

const PLAYBACK_RATES = [0.25, 0.5, 1, 2];

const TELEMETRY_LAYERS: Array<{ id: TelemetryLayer; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'traces', label: 'Traces' },
  { id: 'circle', label: 'Grip' },
  { id: 'readout', label: 'Readout' },
];

function telemetryLayerOn(layer: TelemetryLayer): boolean {
  return settings.telemetryLayers.includes(layer);
}

/** Shows/hides each layer element to match `settings.telemetryLayers`, and
 * the panel itself only when at least one layer is switched on — so turning
 * every layer off clears the dock entirely rather than leaving an empty box
 * floating over the track. */
function syncTelemetryLayers(): void {
  for (const { id } of TELEMETRY_LAYERS) {
    const el = document.querySelector<HTMLElement>(`.telemetry-layer[data-layer="${id}"]`);
    if (el) el.hidden = !telemetryLayerOn(id);
  }
  telemetryPanel.hidden = settings.telemetryLayers.length === 0;
}

function frameOf(r: ReplayState): number {
  return r.timeMs / (GHOST_STRIDE * (1000 / TICK_HZ));
}

const REPLAY_PLAY_ICON =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">' +
  '<path d="M4 2.5v11l10-5.5z"/></svg>';
const REPLAY_PAUSE_ICON =
  '<svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">' +
  '<rect x="4" y="3" width="3" height="10" rx="0.5"/><rect x="9" y="3" width="3" height="10" rx="0.5"/></svg>';

/** Swaps the transport button's icon and accessible name together, so a
 * screen reader announces "Play" / "Pause" rather than a static label that
 * stops matching the glyph the moment playback state changes. */
function updatePlayButton(playing: boolean): void {
  const btn = $('replay-play');
  btn.innerHTML = playing ? REPLAY_PAUSE_ICON : REPLAY_PLAY_ICON;
  btn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
}

function enterReplay(ghost: Ghost): void {
  const model = buildTelemetry(world.track, world.profile, world.corners, ghost);
  replay = { ghost, model, timeMs: 0, playing: true, rate: 1 };
  hidePanels();
  replayBar.hidden = false;
  updatePlayButton(true);
  document.body.classList.add('replaying');
  renderSpeedPicker();
  renderLayerPicker();
  syncTelemetryLayers();
  renderTelemetrySummary(model);
  audio.idle();
  syncReplayBarHeight();
  drawTelemetry();
}

function exitReplay(): void {
  replay = null;
  replayBar.hidden = true;
  telemetryPanel.hidden = true;
  document.body.classList.remove('replaying');
  syncReplayBarHeight();
  if (session.result) resultPanel.hidden = false;
  else showStart();
}

function renderSpeedPicker(): void {
  const host = $('replay-speeds');
  host.replaceChildren();
  for (const rate of PLAYBACK_RATES) {
    const btn = document.createElement('button');
    btn.className = 'speed-chip';
    btn.type = 'button';
    btn.textContent = `${rate}×`;
    btn.setAttribute('aria-pressed', String(replay?.rate === rate));
    btn.addEventListener('click', () => {
      if (!replay) return;
      replay.rate = rate;
      renderSpeedPicker();
    });
    host.append(btn);
  }
}

/** One chip per Data layer, independently add/dropable — clicking a chip
 * toggles just that layer in `settings.telemetryLayers` and persists it, so
 * the choice survives to the next replay. */
function renderLayerPicker(): void {
  const host = $('telemetry-layer-picker');
  host.replaceChildren();
  for (const { id, label } of TELEMETRY_LAYERS) {
    const btn = document.createElement('button');
    btn.className = 'speed-chip';
    btn.type = 'button';
    btn.textContent = label;
    btn.setAttribute('aria-pressed', String(telemetryLayerOn(id)));
    btn.addEventListener('click', () => {
      const on = telemetryLayerOn(id);
      const next = on
        ? settings.telemetryLayers.filter((l) => l !== id)
        : [...settings.telemetryLayers, id];
      settings = updateSettings({ telemetryLayers: next });
      btn.setAttribute('aria-pressed', String(!on));
      syncTelemetryLayers();
      syncReplayBarHeight();
      drawTelemetry();
    });
    host.append(btn);
  }
}

/** Jump the playhead to the braking point of the next or previous corner. */
function jumpCorner(direction: 1 | -1): void {
  if (!replay) return;
  const frames = replay.model.cornerFrames;
  if (frames.length === 0) return;
  const current = frameOf(replay);
  const ordered = direction === 1 ? frames : [...frames].reverse();
  const target =
    ordered.find((c) => (direction === 1 ? c.frame > current + 2 : c.frame < current - 2)) ??
    ordered[0];
  replay.timeMs = target.frame * GHOST_STRIDE * (1000 / TICK_HZ);
  replay.playing = false;
  updatePlayButton(false);
  drawTelemetry();
}

/** Jump the playhead to the single frame furthest behind the ideal lap. */
function jumpToWorst(): void {
  if (!replay) return;
  replay.timeMs = replay.model.worstFrame * GHOST_STRIDE * (1000 / TICK_HZ);
  replay.playing = false;
  updatePlayButton(false);
  drawTelemetry();
}

/** The last corner whose braking zone starts at or before the given frame —
 * used to label the "Worst moment" stat with the corner it happened in. */
function cornerNear(model: TelemetryModel, frame: number): number | null {
  let number: number | null = null;
  for (const c of model.cornerFrames) {
    if (c.frame > frame) break;
    number = c.number;
  }
  return number;
}

/**
 * Renders the whole-lap figures above the trace charts: lap time, total time
 * lost against the ideal, top speed, and the single worst moment (clickable —
 * jumps the playhead there). Computed once per replay, unlike the readout
 * aside below, which tracks wherever the scrub position currently is.
 */
function renderTelemetrySummary(model: TelemetryModel): void {
  const host = $('telemetry-summary');
  host.replaceChildren();

  const stat = (label: string, value: string, cls?: string): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = 'telemetry-stat';
    el.innerHTML = `<span>${label}</span><strong class="${cls ?? ''}">${value}</strong>`;
    return el;
  };

  host.append(stat('Lap', formatTime(model.ghost.timeMs)));

  const totalLost = model.delta[model.delta.length - 1] ?? 0;
  host.append(stat('Vs ideal', formatDelta(totalLost * 1000), totalLost > 0.05 ? 'bad' : 'good'));

  let topSpeed = 0;
  for (let i = 0; i < model.ghost.speed.length; i++) topSpeed = Math.max(topSpeed, model.ghost.speed[i]);
  host.append(stat('Top speed', `${Math.round(topSpeed * 3.6)} km/h`));

  const worstLoss = model.delta[model.worstFrame] ?? 0;
  const corner = cornerNear(model, model.worstFrame);
  const worst = document.createElement('button');
  worst.type = 'button';
  worst.className = 'telemetry-stat worst';
  worst.innerHTML =
    `<span>Worst moment${corner !== null ? ` · T${corner}` : ''}</span>` +
    `<strong class="bad">${formatDelta(worstLoss * 1000)}</strong>`;
  worst.addEventListener('click', jumpToWorst);
  host.append(worst);
}

/**
 * Publishes the replay bar's measured height so the telemetry panel can sit
 * clear of it.
 *
 * The bar wraps to two rows on a narrow screen and its height depends on how
 * the buttons happen to fold, which no stylesheet can predict. Measuring it and
 * writing the value back as a custom property keeps the two from overlapping
 * without hard-coding a number that drifts the moment a control is added.
 */
function syncReplayBarHeight(): void {
  const h = replayBar.hidden ? 0 : replayBar.getBoundingClientRect().height;
  document.documentElement.style.setProperty('--replay-bar-h', `${Math.round(h)}px`);
}

function drawTelemetry(): void {
  syncReplayBarHeight();
  if (!replay || telemetryPanel.hidden) return;
  const frame = frameOf(replay);
  // Only the layers the player switched on: no point paying for a canvas
  // draw or a DOM rebuild the dock isn't showing.
  if (telemetryLayerOn('traces')) {
    drawTraces($<HTMLCanvasElement>('telemetry-traces'), replay.model, frame);
  }
  if (telemetryLayerOn('circle')) {
    drawFrictionCircle($<HTMLCanvasElement>('telemetry-circle'), replay.model, frame);
  }
  if (telemetryLayerOn('readout')) {
    renderReadout(readoutAt(replay.model, frame));
  }
}

function renderReadout(r: TelemetryReadout): void {
  const rows: Array<[string, string, string?]> = [
    ['Speed', `${Math.round(r.speedKph)} km/h`],
    ['Distance', `${Math.round(r.distanceM)} m`],
    ['Lost', `${r.deltaS >= 0 ? '+' : ''}${r.deltaS.toFixed(2)}s`, r.deltaS > 0.05 ? 'bad' : 'good'],
    ['Lateral', `${Math.abs(r.latG).toFixed(2)}g`],
    ['Long.', `${r.longG.toFixed(2)}g`],
    ['Brake', `${Math.round(r.brake * 100)}%`],
    ['Steer', `${Math.round(r.steer * 100)}%`],
    ['Front grip', `${Math.round(r.frontLoad * 100)}%`, r.frontLoad > 0.97 ? 'bad' : undefined],
    ['Rear grip', `${Math.round(r.rearLoad * 100)}%`, r.rearLoad > 0.97 ? 'bad' : undefined],
  ];
  const host = $('telemetry-readout');
  host.replaceChildren();
  for (const [label, value, cls] of rows) {
    const row = document.createElement('div');
    row.className = 'readout-row';
    row.innerHTML = `<span>${label}</span><strong class="${cls ?? ''}">${value}</strong>`;
    host.append(row);
  }
}

function bindReplay(): void {
  const scrub = $<HTMLInputElement>('replay-scrub');
  scrub.addEventListener('input', () => {
    if (!replay) return;
    replay.playing = false;
    updatePlayButton(false);
    replay.timeMs = (Number(scrub.value) / 1000) * replay.ghost.timeMs;
    drawTelemetry();
  });
  $('replay-play').addEventListener('click', () => {
    if (!replay) return;
    replay.playing = !replay.playing;
    updatePlayButton(replay.playing);
  });
  $('replay-prev').addEventListener('click', () => jumpCorner(-1));
  $('replay-next').addEventListener('click', () => jumpCorner(1));
  $('replay-exit').addEventListener('click', exitReplay);

  // Scrubbing directly on the charts is faster than dragging the slider when
  // you already know which spike you want to look at.
  const traces = $<HTMLCanvasElement>('telemetry-traces');
  const seek = (e: PointerEvent): void => {
    if (!replay || e.buttons === 0) return;
    const rect = traces.getBoundingClientRect();
    const t = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    replay.timeMs = t * replay.ghost.timeMs;
    replay.playing = false;
    updatePlayButton(false);
    drawTelemetry();
  };
  traces.addEventListener('pointerdown', (e) => {
    traces.setPointerCapture(e.pointerId);
    seek({ ...e, buttons: 1 } as PointerEvent);
  });
  traces.addEventListener('pointermove', seek);

  window.addEventListener('resize', () => drawTelemetry());
}

// ---------------------------------------------------------------- results

function noteBrake(d: CornerDelta): { text: string; cls: string } {
  if (d.wentOff) return { text: 'off track', cls: 'bad' };
  if (d.lockedUp) return { text: 'locked up', cls: 'bad' };
  if (d.brakeOffset < -5) return { text: `${Math.abs(Math.round(d.brakeOffset))}m early`, cls: 'early' };
  if (d.brakeOffset > 5) return { text: `${Math.round(d.brakeOffset)}m late`, cls: 'late' };
  return { text: 'on point', cls: '' };
}

function renderCornerRows(deltas: CornerDelta[], worst: CornerDelta | null): void {
  const rows = $('corner-rows');
  rows.replaceChildren();
  for (const d of deltas) {
    const row = document.createElement('div');
    row.className = 'corner-row' + (worst && d.number === worst.number ? ' worst-row' : '');
    const note = noteBrake(d);
    row.innerHTML =
      `<span>T${d.number}</span>` +
      `<span class="note ${note.cls}">${note.text}</span>` +
      `<span>${Math.round(d.vMin * 3.6)} km/h</span>` +
      `<span class="lost">${d.lost < 0.005 ? '—' : `+${d.lost.toFixed(2)}s`}</span>`;

    // Straight from "you lost 1.6s at T4" to drilling T4. Without this the mode
    // is only reachable from the start panel and knows nothing about where you
    // are actually slow.
    const drill = document.createElement('button');
    drill.className = 'row-drill';
    drill.type = 'button';
    drill.textContent = 'Drill';
    drill.addEventListener('click', () => {
      const corner = world.corners.find((c) => c.number === d.number);
      if (corner) openDrill(corner);
    });
    row.append(drill);
    rows.append(row);
  }
}

function renderSectors(sectorMs: number[]): void {
  const host = $('result-sectors');
  host.replaceChildren();
  let prevCum = 0;
  let prevBest = 0;
  for (let i = 0; i < sectorMs.length; i++) {
    const split = sectorMs[i] - prevCum;
    prevCum = sectorMs[i];

    let cls = 'sector';
    if (bestSectors && i < bestSectors.length) {
      const bestSplit = bestSectors[i] - prevBest;
      prevBest = bestSectors[i];
      cls += split < bestSplit * 0.999 ? ' purple' : split < bestSplit * 1.005 ? ' green' : ' yellow';
    } else {
      cls += ' purple';
    }

    const el = document.createElement('div');
    el.className = cls;
    el.innerHTML = `<span class="label">S${i + 1}</span><strong>${formatTime(split)}</strong>`;
    host.append(el);
  }
}

function renderAttemptBars(): void {
  const bars = $('attempt-bars');
  bars.replaceChildren();
  const valid = day.attempts.filter((t) => t > 0);
  if (valid.length === 0) return;
  const best = Math.min(...valid);
  const worst = Math.max(...valid);
  for (const t of day.attempts) {
    const bar = document.createElement('div');
    const span = Math.max(1, worst - best);
    bar.className = 'bar' + (t === best ? ' best' : '');
    bar.style.height = `${6 + (1 - (t - best) / span) * 36}px`;
    bar.title = formatTime(t);
    bars.append(bar);
  }
}

async function refreshLeaderboard(): Promise<void> {
  const block = $('leaderboard-block');
  if (!leaderboardEnabled()) {
    block.hidden = true;
    return;
  }
  block.hidden = false;
  $('leaderboard-rename-name').textContent = playerName() || 'Anonymous';
  const rows = $('leaderboard-rows');
  rows.textContent = 'Loading…';
  const page = await fetchLeaderboard(world.key, settings.realism);
  rows.replaceChildren();
  if (page.status !== 'ok' || page.entries.length === 0) {
    rows.textContent = page.message ?? 'No times yet today. Set the benchmark.';
    return;
  }
  for (const entry of page.entries) {
    const row = document.createElement('div');
    row.className = 'lb-row' + (entry.isYou ? ' you' : '');
    row.innerHTML =
      `<span class="lb-rank">${entry.rank}</span>` +
      `<span class="lb-name">${entry.name}</span>` +
      `<span class="lb-time">${formatTime(entry.timeMs)}</span>`;
    rows.append(row);
  }
}

/**
 * Today's board in the menu's Garage column. Same rows as the result panel's
 * copy, which stays the place you land on after a lap; this one exists so the
 * board is visible before one, without a click. The list caps its own height
 * and scrolls, so a long board costs the column nothing.
 */
async function renderMenuLeaderboard(): Promise<void> {
  const block = $('menu-leaderboard');
  if (!leaderboardEnabled()) {
    block.hidden = true;
    return;
  }
  block.hidden = false;
  const rows = $('menu-leaderboard-rows');

  const cacheKey = `${world.key}:${settings.realism}`;
  let promise: Promise<LeaderboardPage>;
  if (leaderboardCache && leaderboardCache.key === cacheKey) {
    promise = leaderboardCache.promise;
  } else {
    promise = fetchLeaderboard(world.key, settings.realism);
    leaderboardCache = { key: cacheKey, realism: settings.realism, promise };
  }

  rows.textContent = 'Loading…';
  const page = await promise;
  rows.replaceChildren();
  if (page.status !== 'ok' || page.entries.length === 0) {
    rows.textContent = page.message ?? 'No times yet today. Set the benchmark.';
    return;
  }
  for (const entry of page.entries) {
    const row = document.createElement('div');
    row.className = 'lb-row' + (entry.isYou ? ' you' : '');
    row.innerHTML =
      `<span class="lb-rank">${entry.rank}</span>` +
      `<span class="lb-name">${entry.name}</span>` +
      `<span class="lb-time">${formatTime(entry.timeMs)}</span>`;
    rows.append(row);
  }
}

// -------------------------------------------------------------- grand prix

/** The stage the world is currently showing, or null if it has drifted from the chain. */
function currentGrandPrixStage(): GrandPrixStage | null {
  if (!gp) return null;
  const stage = gp.stages[gp.index];
  return stage.key === world.key ? stage : null;
}

function exitGrandPrix(): void {
  gp = null;
  reloadWorld(buildWorld(todayKey)); // clears aiRacers, same as any world change
  showStart();
}

/** Points and time banked from every stage completed before the current one. */
function completedStagesSummary(run: GrandPrixRun): { points: number; timeMs: number } {
  let points = 0;
  let timeMs = 0;
  for (let i = 0; i < run.index; i++) {
    points += run.points[i] ?? 0;
    timeMs += run.times[i] ?? 0;
  }
  return { points, timeMs };
}

/**
 * One line of Grand Prix status, shared by the Race-tab banner (before a
 * stage) and the Result-panel banner (after an attempt at it) — the only
 * difference between the two is whether `positions`/`points` has been filled
 * in yet for the stage in progress.
 */
function gpStatusText(run: GrandPrixRun): string {
  const base = `Grand Prix · Stage ${run.index + 1} of ${run.stages.length}`;
  // Room-mode stages never populate positions/points locally — those come
  // from the room's server standings, not a local AI-relative finish.
  if (run.roomCode) return base;
  const parts: string[] = [];
  const qPos = run.qualiPositions[run.index];
  if (run.format === 'quali' && qPos !== null && run.gridOrders[run.index]) {
    parts.push(`qualified P${qPos}`);
  }
  const pos = run.positions[run.index];
  const pts = run.points[run.index];
  if (pos !== null && pts !== null) parts.push(`best P${pos} (+${pts} pts)`);
  if (run.index > 0) parts.push(`${completedStagesSummary(run).points} pts so far`);
  return parts.length ? `${base} · ${parts.join(' · ')}` : base;
}

/** Banner on the Race tab: only visible while the current world is a live GP stage. */
function renderGrandPrixStartBanner(): void {
  const banner = $('gp-banner');
  if (gp && !currentGrandPrixStage()) {
    // The player navigated to a different circuit mid-chain — the chain no
    // longer means anything, so it ends quietly rather than lying about it.
    gp = null;
    aiRacers = [];
    quali = null;
    qualiRoster = null;
  }
  if (!gp) {
    banner.hidden = true;
    return;
  }
  banner.hidden = false;
  $('gp-banner-text').textContent = gpStatusText(gp);
}

function renderGrandPrixIntro(): void {
  const stages = buildGrandPrixStages(new Date());
  const list = $('grandprix-stage-list');
  list.replaceChildren();
  for (const stage of stages) {
    const row = document.createElement('div');
    row.className = 'gp-stage-row';
    row.innerHTML =
      `<span class="gp-stage-label">${stage.label}</span>` +
      (stage.label === stage.key ? '' : `<span>${stage.key}</span>`);
    list.append(row);
  }
  buildChips(
    'grandprix-mode-list',
    [
      { id: 'solo', label: 'Solo · AI grid' },
      { id: 'quali', label: 'Solo · Quali + GP' },
      { id: 'room', label: 'Room · friends' },
    ],
    () => settings.gpMode,
    (id) => {
      settings = updateSettings({ gpMode: id as Settings['gpMode'] });
      renderGrandPrixModeDetail();
    },
  );
  renderGrandPrixModeDetail();
}

/** Toggles the Solo/Room detail under the Grand Prix format chips, and keeps
 *  `Start Grand Prix` disabled while Room is selected but no room is joined —
 *  there is nothing to start yet. */
function renderGrandPrixModeDetail(): void {
  // Quali shares the solo detail — it needs the same "how many AI cars" choice.
  const gridBased = settings.gpMode === 'solo' || settings.gpMode === 'quali';
  $('grandprix-solo-detail').hidden = !gridBased;
  $('grandprix-room-detail').hidden = settings.gpMode !== 'room';
  $('grandprix-quali-note').hidden = settings.gpMode !== 'quali';

  if (gridBased) {
    $<HTMLButtonElement>('grandprix-start-button').disabled = false;
    buildChips(
      'grandprix-grid-list',
      GRID_SIZES.map((n) => ({ id: String(n), label: `${n} AI cars` })),
      () => String(settings.gpGridSize),
      (id) => {
        settings = updateSettings({ gpGridSize: Number(id) });
      },
    );
  } else {
    renderRoomJoinOrStatus();
  }
}

/** Room detail: code entry/create when not yet in a room, else the room code
 *  and a live standings summary. */
function renderRoomJoinOrStatus(): void {
  const code = roomCode();
  $('grandprix-room-join').hidden = Boolean(code);
  $('grandprix-room-status').hidden = !code;
  $<HTMLButtonElement>('grandprix-start-button').disabled = !code;
  if (code) {
    $('grandprix-room-code').textContent = code;
    void refreshRoomStandingsSummary(code);
  }
}

async function refreshRoomStandingsSummary(code: string): Promise<void> {
  const summary = $('grandprix-room-summary');
  const detail = $<HTMLDetailsElement>('grandprix-room-standings-detail');
  summary.textContent = 'Loading standings…';
  detail.hidden = true;

  const page = await fetchRoomStandings(code, settings.realism);
  if (page.status !== 'ok') {
    summary.textContent = page.message ?? 'Could not reach the room.';
    return;
  }

  const count = page.standings.length;
  const mine = page.standings.find((s) => s.isYou);
  const myRank = mine ? page.standings.indexOf(mine) + 1 : null;
  summary.textContent =
    count <= 1
      ? 'Just you so far — share the code to bring friends in.'
      : mine && myRank
        ? `${count} racers · you're P${myRank} · ${mine.totalPoints} pts`
        : `${count} racers`;

  detail.hidden = false;
  renderRoomStandingsTable($('grandprix-room-standings-rows'), page);
}

/** Renders a room's standings as `.lb-row`s — same look as the daily
 *  leaderboard, rank/name/points instead of rank/name/time. Shared by the
 *  in-progress summary above and the Grand Prix Complete screen. */
function renderRoomStandingsTable(host: HTMLElement, page: RoomStandingsPage): void {
  host.replaceChildren();
  if (page.status !== 'ok' || page.standings.length === 0) {
    host.textContent = page.message ?? 'No one has raced yet.';
    return;
  }
  page.standings.forEach((row: RoomStandingRow & { isYou: boolean }, i: number) => {
    const el = document.createElement('div');
    el.className = 'lb-row' + (row.isYou ? ' you' : '');
    el.innerHTML =
      `<span class="lb-rank">${i + 1}</span>` +
      `<span class="lb-name">${row.name}</span>` +
      `<span class="lb-time">${row.totalPoints} pts</span>`;
    host.append(el);
  });
}

function beginGrandPrix(): void {
  const gridSize = (GRID_SIZES as readonly number[]).includes(settings.gpGridSize)
    ? (settings.gpGridSize as GridSize)
    : DEFAULT_GRID_SIZE;
  const room = settings.gpMode === 'room' ? roomCode() : null;
  const format = settings.gpMode === 'quali' ? 'quali' : 'race';
  gp = createGrandPrix(new Date(), gridSize, room, format);
  enterStage();
}

function advanceGrandPrix(): void {
  if (!gp) return;
  gp.index = Math.min(gp.index + 1, gp.stages.length - 1);
  enterStage();
}

/** Loads the current stage's circuit, then routes to qualifying (quali format,
 *  grid not yet set) or straight to the pre-race screen. */
function enterStage(): void {
  if (!gp) return;
  const stage = gp.stages[gp.index];
  reloadWorld(buildWorld(stage.key, { label: stage.label, width: GP_TRACK_WIDTH, length: 'standard' }));
  if (gp.format === 'quali' && !gp.gridOrders[gp.index]) enterQuali();
  else showStart();
}

function renderGrandPrixSummary(): void {
  if (!gp) return;
  $('gp-result-stages').className = 'gp-stage-list gp-stage-list-full';
  const points = grandPrixPointsTotal(gp) ?? 0;
  $('gp-result-total').textContent = `${points} pts`;
  $('gp-result-time-total').textContent = `${formatTime(grandPrixTotal(gp) ?? 0)} total time`;

  const list = $('gp-result-stages');
  list.replaceChildren();
  const isQuali = gp.format === 'quali';
  list.classList.toggle('with-quali', isQuali);
  if (isQuali) {
    const bestQ = Math.min(
      ...gp.qualiPositions.filter((q): q is number => q !== null),
    );
    if (Number.isFinite(bestQ)) {
      $('gp-result-time-total').textContent += ` · best quali P${bestQ}`;
    }
  }
  gp.stages.forEach((stage, i) => {
    const row = document.createElement('div');
    row.className = 'gp-stage-row done';
    const pos = gp!.positions[i];
    const pts = gp!.points[i];
    const time = gp!.times[i];
    const qpos = gp!.qualiPositions[i];
    row.innerHTML =
      `<span class="gp-stage-label">${stage.label}</span>` +
      (isQuali
        ? `<span class="gp-stage-quali">${qpos === null ? '—' : `Q P${qpos}`}</span>`
        : '') +
      `<span class="gp-stage-pos">${pos === null ? '—' : `P${pos}`}</span>` +
      `<span class="gp-stage-pts">${pts === null ? '—' : `+${pts} pts`}</span>` +
      `<span class="gp-stage-time">${time === null ? '—' : formatTime(time)}</span>`;
    list.append(row);
  });
}

function finishGrandPrix(): void {
  if (!gp) return;
  if (gp.roomCode) {
    void renderRoomGrandPrixSummary(gp.roomCode);
  } else {
    renderGrandPrixSummary();
  }
  hidePanels();
  grandprixResultPanel.hidden = false;
}

async function renderRoomGrandPrixSummary(code: string): Promise<void> {
  const stagesHost = $('gp-result-stages');
  stagesHost.className = 'leaderboard';
  stagesHost.textContent = 'Loading standings…';
  $('gp-result-total').textContent = '—';
  $('gp-result-time-total').textContent = '';

  const page = await fetchRoomStandings(code, settings.realism);
  if (page.status !== 'ok') {
    stagesHost.textContent = page.message ?? 'Could not reach the room.';
    return;
  }

  const mine = page.standings.find((s) => s.isYou);
  const myRank = mine ? page.standings.indexOf(mine) + 1 : null;
  $('gp-result-total').textContent = mine ? `${mine.totalPoints} pts` : '—';
  $('gp-result-time-total').textContent =
    myRank !== null ? `P${myRank} of ${page.standings.length} in the room` : '';
  renderRoomStandingsTable(stagesHost, page);
}

/**
 * Where the player finished among the grid, the moment their own lap ends.
 * Any AI kart not yet finished is — in a single-lap race — necessarily still
 * behind, so only already-finished, faster AI count against the player.
 */
function gpFinishPosition(): number {
  const mine = session.result?.timeMs ?? Infinity;
  let ahead = 0;
  for (const r of aiRacers) {
    if (r.session.phase === 'finished' && (r.session.result?.timeMs ?? Infinity) < mine) ahead++;
  }
  return ahead + 1;
}

async function submitRoomStageAndRefresh(code: string, r: NonNullable<Session['result']>): Promise<void> {
  // Same rule as the daily board — a room's standings compare its members'
  // laps against each other, so an auto-braked one does not belong in them.
  if (settings.autoBrake) return;
  const payload = buildSubmission(world.key, world.seed, settings.livery, settings.realism, r, TICK_HZ);
  await submitRoomStage(code, payload);
}

/** Shared by both branches of `renderGrandPrixResultBanner`: turns the primary
 *  action into a retry until the stage is done, then into the advance step. */
function setGrandPrixResultActions(
  run: GrandPrixRun,
  stageDone: boolean,
  retryButton: HTMLButtonElement,
  gpRetryButton: HTMLButtonElement,
  actions: HTMLElement,
): void {
  if (!stageDone) {
    retryButton.textContent = 'Retry stage';
    retryButton.onclick = beginRun;
    gpRetryButton.hidden = true;
    actions.classList.remove('gp-active');
  } else {
    retryButton.textContent = isLastGrandPrixStage(run) ? 'Finish Grand Prix ▸' : 'Next circuit ▸';
    retryButton.onclick = isLastGrandPrixStage(run) ? finishGrandPrix : advanceGrandPrix;
    gpRetryButton.hidden = false;
    gpRetryButton.textContent = 'Retry stage';
    gpRetryButton.onclick = beginRun;
    actions.classList.add('gp-active');
  }
}

/** Result panel: records the stage time and turns the primary action into the next step. */
function renderGrandPrixResultBanner(r: NonNullable<Session['result']>): void {
  const banner = $('gp-result-banner');
  const retryButton = $<HTMLButtonElement>('retry-button');
  const gpRetryButton = $<HTMLButtonElement>('gp-retry-button');
  const actions = $('result-actions');

  if (!gp || !currentGrandPrixStage()) {
    banner.hidden = true;
    gpRetryButton.hidden = true;
    actions.classList.remove('gp-active');
    retryButton.textContent = 'Go again';
    retryButton.onclick = beginRun;
    return;
  }

  if (quali) {
    applyPlayerLap(quali, r);
    renderQualiBoard();
    banner.hidden = false;
    $('gp-result-banner-text').textContent = qualiBannerText(quali);
    gpRetryButton.hidden = true;
    actions.classList.remove('gp-active');
    retryButton.textContent = 'Back to timing ▸';
    retryButton.onclick = backToTiming;
    return;
  }

  if (gp.roomCode) {
    if (r.valid) {
      recordGrandPrixTime(gp, r.timeMs);
      void submitRoomStageAndRefresh(gp.roomCode, r);
    }
    banner.hidden = false;
    $('gp-result-banner-text').textContent = gpStatusText(gp);
    setGrandPrixResultActions(gp, gp.times[gp.index] !== null, retryButton, gpRetryButton, actions);
    return;
  }

  if (r.valid) {
    recordGrandPrixAttempt(gp, r.timeMs, gpFinishPosition());
  }

  banner.hidden = false;
  $('gp-result-banner-text').textContent = gpStatusText(gp);
  setGrandPrixResultActions(gp, gp.points[gp.index] !== null, retryButton, gpRetryButton, actions);
}

// ---------------------------------------------------------------- qualifying

/** Share of a clean lap spent in each of the three timed sectors, from the
 *  solved ideal profile — used to split a generated AI lap into sector times. */
function sectorTimeFractions(): number[] {
  const { vIdeal } = world.profile;
  const { sectorStarts, center, spacing } = world.track;
  const n = center.length;
  const bounds = [...sectorStarts, sectorStarts[0]];
  const times: number[] = [];
  for (let s = 0; s < 3; s++) {
    let t = 0;
    let i = bounds[s];
    for (let guard = 0; guard < n; guard++) {
      t += spacing / Math.max(0.5, vIdeal[i]);
      i = wrapIndex(i + 1, n);
      if (i === bounds[s + 1]) break;
    }
    times.push(t);
  }
  const total = times.reduce((a, b) => a + b, 0) || 1;
  return times.map((t) => t / total);
}

/** Fastest speed anywhere on the ideal profile, km/h — the speed-trap ceiling. */
function profileTopKmh(): number {
  let mx = 0;
  for (const v of world.profile.vIdeal) mx = Math.max(mx, v);
  return mx * 3.6;
}

function enterQuali(): void {
  if (!gp) return;
  const roster = buildAIRoster({
    track: world.track,
    profile: world.profile,
    corners: world.corners,
    weather: world.weather,
    realism: activeRealism(),
    tyreModel: settings.tyreModel,
    seed: world.seed,
    count: gp.gridSize,
    excludeLiveryId: settings.livery,
  });
  qualiRoster = roster;
  quali = createQuali(
    world.key,
    world.seed,
    roster.racers.map((r) => r.name),
    roster.paces,
    {
      idealLapMs: world.profile.idealLapTime * 1000,
      sectorFractions: sectorTimeFractions(),
      profileMaxKmh: profileTopKmh(),
    },
    day.bestMs,
  );
  aiRacers = [];
  hidePanels();
  qualiPanel.hidden = false;
  renderQualiBoard();
}

function runQualiLap(): void {
  if (!quali || segmentExhausted(quali)) return;
  beginRun();
}

function backToTiming(): void {
  if (!quali) return;
  hidePanels();
  qualiPanel.hidden = false;
  renderQualiBoard();
}

function doneWithSegment(): void {
  if (!quali) return;
  advanceSegment(quali);
  if (isQualiComplete(quali)) {
    finishQuali();
    return;
  }
  renderQualiBoard();
}

function finishQuali(): void {
  if (!gp || !quali) return;
  const cls = classification(quali);
  gp.qualiPositions[gp.index] = cls.findIndex((d) => d.isPlayer) + 1;
  gp.gridOrders[gp.index] = gridRowsFor(cls, qualiRoster?.racers.length ?? gp.gridSize);
  quali = null;
  showStart();
  // showStart doesn't repopulate the Race tab; the GP banner still reads the
  // pre-qualifying text until we refresh it.
  renderGrandPrixStartBanner();
}

const SEGMENT_NEXT: Record<string, string> = { Q1: 'Q2', Q2: 'Q3' };

function qualiBannerText(state: QualiState): string {
  const field = orderedField(state);
  const pRow = field.findIndex((d) => d.isPlayer);
  const where = pRow >= 0 ? `P${pRow + 1} of ${field.length}` : 'no time set';
  return `Grand Prix · Stage ${(gp?.index ?? 0) + 1}/3 · ${state.segment} · ${where}`;
}

function renderQualiBoard(): void {
  if (!quali) return;
  const state = quali;
  const field = orderedField(state);
  const eliminated = state.drivers
    .filter((d) => d.status === 'out')
    .sort((a, b) => (a.outLapMs ?? Infinity) - (b.outLapMs ?? Infinity));
  const cut = cutCount(state);
  const pole = field[0]?.bestLapMs ?? null;

  $('quali-title').textContent = `Grand Prix · Stage ${(gp?.index ?? 0) + 1} of 3 — Qualifying`;
  $('quali-heading').textContent = state.segment;
  $('quali-sub').textContent =
    (state.segment === 'Q3'
      ? `${field.length} drivers · fighting for pole`
      : `${field.length} drivers · slowest ${cut} eliminated`) +
    ` · ${state.playerAttemptsLeft} of ${3} laps left`;

  const board = $('quali-board');
  board.replaceChildren();
  board.append(rowEl('POS', 'DRIVER', 'BEST', 'GAP', 'INT', 'S1 · S2 · S3', 'TRAP', '', true));
  field.forEach((d, i) => {
    const inCut = state.segment !== 'Q3' && i >= field.length - cut;
    board.append(qualiRow(d, i + 1, pole, field[i - 1] ?? null, inCut ? 'q-cut' : ''));
  });
  eliminated.forEach((d, i) => {
    board.append(qualiRow(d, field.length + i + 1, pole, null, 'q-out'));
  });

  renderQualiDetail(state);

  const runBtn = $<HTMLButtonElement>('quali-run');
  const doneBtn = $<HTMLButtonElement>('quali-done');
  const exhausted = segmentExhausted(state);
  runBtn.disabled = exhausted;
  runBtn.textContent = exhausted ? 'No laps left this segment' : `Run lap · ${state.playerAttemptsLeft} left`;
  runBtn.classList.toggle('primary', !exhausted);
  runBtn.classList.toggle('ghost', exhausted);
  doneBtn.classList.toggle('primary', exhausted);
  doneBtn.classList.toggle('ghost', !exhausted);
  doneBtn.textContent =
    state.segment === 'Q3' ? 'Finish qualifying ▸' : `Done — start ${SEGMENT_NEXT[state.segment]} ▸`;
}

function rowEl(
  pos: string,
  name: string,
  best: string,
  gap: string,
  int: string,
  sectors: string,
  trap: string,
  status: string,
  head = false,
): HTMLElement {
  const el = document.createElement('div');
  el.className = head ? 'q-row q-head' : 'q-row';
  el.innerHTML =
    `<span class="q-pos">${pos}</span>` +
    `<span class="q-name">${name}</span>` +
    `<span class="q-best">${best}</span>` +
    `<span class="q-gap">${gap}</span>` +
    `<span class="q-int">${int}</span>` +
    `<span class="q-sectors">${sectors}</span>` +
    `<span class="q-trap">${trap}</span>` +
    `<span class="q-status">${status}</span>`;
  return el;
}

function qualiRow(
  d: QualiDriver,
  pos: number,
  pole: number | null,
  above: QualiDriver | null,
  cls: string,
): HTMLElement {
  const best = d.bestLapMs === null ? '—' : formatTime(d.bestLapMs);
  const gap =
    d.bestLapMs === null || pole === null || d.bestLapMs === pole
      ? d.bestLapMs !== null && d.bestLapMs === pole
        ? 'pole'
        : '—'
      : formatDelta(d.bestLapMs - pole);
  const int =
    d.bestLapMs === null || !above || above.bestLapMs === null
      ? '—'
      : formatDelta(d.bestLapMs - above.bestLapMs);
  let sectors = '—';
  if (d.sectorMs && d.sectorMs.length === 3) {
    const s = [d.sectorMs[0], d.sectorMs[1] - d.sectorMs[0], d.sectorMs[2] - d.sectorMs[1]];
    sectors = s.map((x) => (x / 1000).toFixed(2)).join(' · ');
  }
  const trap = d.speedTrapKmh === null ? '—' : `${Math.round(d.speedTrapKmh)}`;
  const status = d.status === 'out' ? `OUT ${d.outAt}` : d.status === 'through' ? 'THRU' : '';
  const el = rowEl(
    String(pos),
    d.isPlayer ? 'You' : d.name,
    best,
    gap,
    int,
    sectors,
    trap,
    status,
  );
  if (cls) el.classList.add(cls);
  if (d.isPlayer) el.classList.add('you');
  return el;
}

function renderQualiDetail(state: QualiState): void {
  const host = $('quali-detail-body');
  host.replaceChildren();
  const p = state.drivers.find((d) => d.isPlayer);
  if (!p || !p.lastMicroSplitMs || !p.bestMicroSplitMs) {
    host.innerHTML = `<p class="hint">Run a lap to see your microsector breakdown.</p>`;
    return;
  }

  const colors = microStripColors(p);
  const strip = document.createElement('div');
  strip.className = 'q-strip';
  colors.forEach((c) => {
    const seg = document.createElement('span');
    seg.className = `q-micro ${c}`;
    strip.append(seg);
  });
  host.append(strip);

  const theo = theoreticalBestMs(p);
  const line = document.createElement('p');
  line.className = 'q-theo';
  if (theo !== null && p.bestLapMs !== null) {
    const under = (p.bestLapMs - theo) / 1000;
    line.innerHTML =
      `Theoretical best <strong>${formatTime(theo)}</strong>` +
      (under > 0.01 ? ` — ${under.toFixed(2)}s under your best lap` : '');
  } else if (theo !== null) {
    line.innerHTML = `Theoretical best <strong>${formatTime(theo)}</strong>`;
  }
  host.append(line);

  const trapLine = document.createElement('p');
  trapLine.className = 'hint';
  trapLine.textContent =
    p.speedTrapKmh !== null ? `Your speed trap: ${Math.round(p.speedTrapKmh)} km/h` : '';
  host.append(trapLine);

  const table = document.createElement('div');
  table.className = 'q-micro-table';
  table.innerHTML = `<div class="q-mt-head"><span>#</span><span>This lap</span><span>Best</span><span>Δ</span></div>`;
  p.lastMicroSplitMs.forEach((t, i) => {
    const best = p.bestMicroSplitMs![i];
    const delta = t - best;
    const row = document.createElement('div');
    row.className = `q-mt-row ${colors[i]}`;
    row.innerHTML =
      `<span>${i + 1}</span>` +
      `<span>${(t / 1000).toFixed(3)}</span>` +
      `<span>${(best / 1000).toFixed(3)}</span>` +
      `<span>${delta <= 0.0005 ? '—' : '+' + (delta / 1000).toFixed(3)}</span>`;
    table.append(row);
  });
  host.append(table);
}

function showResult(): void {
  const r = session.result;
  if (!r) return;

  const previousBest = day.bestMs;
  const isPb = r.valid && (previousBest === null || r.timeMs < previousBest);

  const medal = medalFor(r.timeMs, r.idealMs);
  lastObjectiveResults = evaluateObjectives(dailyObjectives, r);

  if (r.valid) {
    day.attempts.push(r.timeMs);
    if (day.attempts.length > 60) day.attempts.shift();

    // Best *individual* sectors accumulate across every attempt, so the optimal
    // lap improves even on a run that was otherwise a write-off.
    day.bestSplits = mergeBestSplits(day.bestSplits, splitsOf(r.sectorMs));

    if (isBetterMedal(medal.id, day.medal as never)) day.medal = medal.id;
    for (const o of lastObjectiveResults) {
      if (o.done && !day.objectives.includes(o.objective.id)) day.objectives.push(o.objective.id);
    }

    if (isPb) {
      day.bestMs = r.timeMs;
      day.ghostMs = r.timeMs;
      day.ghostB64 = encodeGhost(
        r.ghost,
        world.track.bounds.minX,
        world.track.bounds.minY,
        Math.max(
          world.track.bounds.maxX - world.track.bounds.minX,
          world.track.bounds.maxY - world.track.bounds.minY,
        ),
      );
      day.bestSectors = r.sectorMs;
      bestGhost = r.ghost;
      bestSectors = r.sectorMs;
      audio.blip(880);
    }
    saveDay(day, recordMode());
    if (world.isToday) recordLap(world.key, r.timeMs, r.idealMs);
    if (world.isToday) void submitAndRefresh(r);
  }

  $('result-headline').classList.toggle('personal-best', isPb);
  const scope = isPb ? 'PERSONAL BEST' : r.valid ? 'LAP COMPLETE' : 'LAP INVALID';
  const medalTag = medal.id === 'none' ? '' : ` · ${medal.name.toUpperCase()}`;
  const label = $('result-label');
  label.textContent = `${scope}${medalTag}`;
  label.style.color = medal.id === 'none' ? '' : medal.color;
  $('result-time').textContent = formatTime(r.timeMs);

  const deltaEl = $('result-delta');
  if (previousBest !== null && r.valid) {
    const d = r.timeMs - previousBest;
    deltaEl.textContent = formatDelta(d);
    deltaEl.className = 'delta ' + (d < 0 ? 'faster' : 'slower');
  } else {
    deltaEl.textContent = '';
    deltaEl.className = 'delta';
  }

  const invalid = $('result-invalid');
  invalid.hidden = r.valid;
  invalid.textContent = r.reason;
  $('result-approx').hidden = !r.tyreModel;

  $('result-best').textContent = day.bestMs === null ? '—' : formatTime(day.bestMs);
  $('result-ideal').textContent = formatTime(r.idealMs);

  // Optimal lap: your best sectors summed. A time you have already driven every
  // part of, just never all at once — which makes it feel reachable in a way
  // the solver's ideal never does.
  const optimal = optimalLap(day.bestSplits);
  $('result-optimal').textContent = optimal === null ? '—' : formatTime(optimal);
  const hint = $('optimal-hint');
  if (optimal !== null && day.bestMs !== null && day.bestMs - optimal > 50) {
    hint.hidden = false;
    hint.textContent =
      `Your best sectors add up to ${formatTime(optimal)} — ` +
      `${((day.bestMs - optimal) / 1000).toFixed(2)}s under your best lap. ` +
      `You have driven every part of it already.`;
  } else {
    hint.hidden = true;
  }

  renderObjectiveList($('result-objectives'), lastObjectiveResults);
  $('result-objectives-block').hidden = dailyObjectives.length === 0;

  const worstEl = $('result-worst');
  worstEl.replaceChildren();
  if (r.worst && r.worst.lost > 0.02) {
    const text = document.createElement('span');
    text.innerHTML = `Biggest loss: <strong>${describeWorst(r.worst)}</strong> &mdash; ${r.worst.lost.toFixed(2)}s`;
    worstEl.append(text);

    // The corner-row Drill button requires scrolling down to find the same
    // row again; this is the same one click from the headline callout itself.
    const worstNumber = r.worst.number;
    const drill = document.createElement('button');
    drill.className = 'row-drill worst-drill';
    drill.type = 'button';
    drill.textContent = 'Drill';
    drill.addEventListener('click', () => {
      const corner = world.corners.find((c) => c.number === worstNumber);
      if (corner) openDrill(corner);
    });
    worstEl.append(drill);
    worstEl.hidden = false;
  } else {
    worstEl.hidden = true;
  }

  lastResultDeltas = r.deltas;
  renderSectors(r.sectorMs);
  renderCornerRows(r.deltas, r.worst);
  renderAttemptBars();
  renderGrandPrixResultBanner(r);

  hidePanels();
  resultPanel.hidden = false;
}

async function submitAndRefresh(r: NonNullable<Session['result']>): Promise<void> {
  // A board is only worth reading if every row was driven under the same rules.
  // `mode` shards it by realism for exactly that reason; automatic braking is
  // not a realism mode, so there is no shard for it to go in and it stays off.
  if (!leaderboardEnabled() || settings.autoBrake) return;
  const payload = buildSubmission(world.key, world.seed, settings.livery, settings.realism, r, TICK_HZ);
  await submitRun(payload);
  // Your own lap just changed the board, so the menu column's cached copy is
  // stale — drop it and let the next menu visit refetch.
  leaderboardCache = null;
  await refreshLeaderboard();
  void renderMenuLeaderboard();
}

// ------------------------------------------------------------------ share

async function openShare(): Promise<void> {
  const r = session.result;
  if (!r) return;
  const card = renderShareCard(world.track, r, {
    dateKey: world.label,
    mode: realismById(settings.realism).name,
    weather: world.weather.name,
    liveryColor: liveryById(settings.livery).body,
  });
  $<HTMLImageElement>('share-image').src = card.toDataURL('image/png');
  hidePanels();
  sharePanel.hidden = false;

  $('share-copy').onclick = async () => {
    const blob = await shareCardBlob(card);
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flash($('share-copy'), 'Copied');
    } catch {
      flash($('share-copy'), 'Copy blocked');
    }
  };
  $('share-download').onclick = () => {
    const a = document.createElement('a');
    a.href = card.toDataURL('image/png');
    a.download = `karting-${world.key}.png`;
    a.click();
  };
}

function flash(btn: HTMLElement, text: string): void {
  const original = btn.textContent;
  btn.textContent = text;
  setTimeout(() => (btn.textContent = original), 1400);
}

// ------------------------------------------------------------- run control

/**
 * Fresh AI grid for this attempt, or none outside a live Grand Prix stage.
 *
 * Also places the player's own kart — grid order is qualifying-lite (see
 * `buildAIGrid`), ranked by the player's stored best for this exact circuit,
 * so pole is earned, not given. Must run after `session.reset()` has already
 * placed the kart at the line, since this repositions it.
 */
function setUpAIGrid(): void {
  // A room stage drives solo, exactly like a normal daily lap — real
  // positions/points come from the room's server standings, not AI bots.
  // A qualifying lap is solo too — the field only exists on the timing board.
  if (!gp || !currentGrandPrixStage() || gp.roomCode || quali) {
    aiRacers = [];
    return;
  }
  // In quali format the grid is whatever qualifying decided; otherwise it is
  // the pace-estimated order. `buildAIRoster` is deterministic per seed, so the
  // field here is the exact one the timing board showed.
  const { racers, playerRow } = buildAIGrid({
    track: world.track,
    profile: world.profile,
    corners: world.corners,
    weather: world.weather,
    realism: activeRealism(),
    tyreModel: settings.tyreModel,
    seed: world.seed,
    count: gp.gridSize,
    excludeLiveryId: settings.livery,
    playerBestMs: day.bestMs,
    gridOrder: gp.gridOrders[gp.index] ?? undefined,
  });
  aiRacers = racers;

  const pose = gridSlotPose(world.track, playerRow);
  session.kart.x = pose.x;
  session.kart.y = pose.y;
  session.kart.heading = pose.heading;
}

const GP_LIGHT_COUNT = 5;
const GP_LIGHT_INTERVAL = 0.4; // seconds between each light illuminating
const GP_LIGHTS_ON_DURATION = GP_LIGHT_COUNT * GP_LIGHT_INTERVAL;

/**
 * All five lights always finish illuminating before the pause that follows
 * them — same shape as a real start: on, on, on, on, on, then an
 * unpredictable beat before they all go out together. Randomised fresh each
 * attempt so the drop can never be memorised.
 */
function randomGpStartHold(): number {
  return GP_LIGHTS_ON_DURATION + 0.3 + Math.random() * 0.9;
}

/** Total hold this attempt launched with, for the lights' fill fraction. Only
 *  meaningful while `aiRacers.length > 0`. */
let gpHoldSeconds = 0;

function beginRun(): void {
  if (session.phase === 'running') return;
  // Every run starts from a standstill — a rolling start dumped the kart at
  // ideal-lap speed on the line, which made the first corner arrive at full
  // pace with no run-up and read as sudden oversteer. A standing start is also
  // what a real kart session is: accelerate away, build speed into turn one.
  session.reset(false);
  session.realism = activeRealism();
  session.tyreModel = settings.tyreModel;
  input.reset();
  effects.clear();
  prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
  renderer.snap(session.kart.x, session.kart.y, session.kart.heading);
  hidePanels();
  replayBar.hidden = true;
  replay = null;
  lastCuedCorner = -1;
  setUpAIGrid();
  // Only a Grand Prix race gets a start sequence — see P2 in docs/menu-plan.md:
  // the daily retry loop must stay exactly one input away, with nothing added
  // that lengthens it.
  gpHoldSeconds = aiRacers.length > 0 ? randomGpStartHold() : 0;
  session.launch(gpHoldSeconds);
  if (aiRacers.length > 0) launchAIGrid(aiRacers, gpHoldSeconds);
  audio.start();
}

// ----------------------------------------------------------------- loops

function tick(): void {
  if (replay || session.phase !== 'running') return;
  prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
  // One sample per tick — it steps the keyboard ramp and reads the pad, so
  // asking twice would double both.
  const { steerByte, brakeByte: pedal } = input.sample();
  // Automatic braking takes whichever is firmer, its demand or the player's.
  // Raising the byte *here*, before the session sees it, is what puts the
  // assisted input in the recorded trace: a replay of that trace reproduces the
  // lap exactly and the ghost, telemetry and submission format are unchanged.
  const brakeByte = settings.autoBrake
    ? Math.max(pedal, quantiseBrake(autoBraker().sample(session.kart)))
    : pedal;

  let playerDrag = 1;
  let aiDrag: number[] | undefined;
  let field: KartState[] | undefined;
  if (aiRacers.length > 0) {
    field = [session.kart, ...aiRacers.map((r) => r.session.kart)];
    const scales = computeSlipstream(field);
    playerDrag = scales[0];
    aiDrag = scales.slice(1);
  }

  const finished = session.tick(steerByte, brakeByte, playerDrag);

  if (field) {
    tickAIGrid(aiRacers, aiDrag, field);
    resolveKartCollisions(field);
  }

  if (!finished) return;
  if (session.drill) onDrillComplete();
  else showResult();
}

function render(alpha: number, frameDt: number): void {
  renderer.resize();
  resizeHud();
  hudCtx!.save();
  hudCtx!.setTransform(1, 0, 0, 1, 0, 0);
  hudCtx!.clearRect(0, 0, hudCanvas.width, hudCanvas.height);
  hudCtx!.restore();

  if (replay) {
    renderReplay(frameDt);
    return;
  }

  const k = session.kart;
  const running = session.phase === 'running';
  const x = running ? lerp(prev.x, k.x, alpha) : k.x;
  const y = running ? lerp(prev.y, k.y, alpha) : k.y;
  const heading = running ? lerpAngle(prev.heading, k.heading, alpha) : k.heading;
  const speed = speedOf(k);
  const motion = settings.reducedMotion ? 0.35 : 1;
  // Same speed/SCALE_REF ramp the camera itself pulls back on, so the 2D
  // edge vignette and speed streaks read as one system with the zoom.
  const speedFx = clamp(speed / Camera.SCALE_REF, 0, 1) * motion;

  renderer.follow(x, y, heading, speed, frameDt);

  if (running) {
    const slide = Math.abs(k.slip);
    if (k.locked) {
      effects.emitSmoke(x, y, k.vx, k.vy, Math.round(2 * motion));
      effects.markSkid(k, 0.9);
    } else if (slide > 2.4) {
      effects.markSkid(k, clamp((slide - 2.4) / 4, 0, 0.7));
      if (slide > 4) effects.emitSmoke(x, y, k.vx, k.vy, Math.round(1 * motion));
    } else {
      effects.markSkid(k, 0);
    }
    if (!session.onTrack && speed > 4) effects.emitDust(x, y, k.vx, k.vy, Math.round(2 * motion));
    if (session.hitFlash) {
      const imp = session.impact;
      const burst = imp
        ? Math.round(clamp(imp.impactSpeed / 4, 2, 8) * motion)
        : Math.round(4 * motion);
      effects.emitSparks(x, y, k.vx, k.vy, burst);
      if (imp) audio.thud(imp.headOn);
      session.hitFlash = false;
    }

    maybeBrakeCue();
    audio.update({
      speed,
      rpm01: clamp(speed / CAR_TOP_SPEED, 0, 1),
      throttle: session.liveControls.throttle,
      slip: slide,
      locked: k.locked,
      onKerb: session.currentKerb > 0.15,
      offTrack: !session.onTrack,
    });
  } else {
    audio.idle();
  }
  effects.update(frameDt);

  // In a drill the target is the segment ghost, not your full-lap best.
  const ghost = session.drill ? drillGhost : activeGhost();
  let ghostSpeed: number | null = null;
  let ghostPoseFrame: WorldFrame['ghost'] = null;
  if (ghost && running) {
    const pose = ghostPose(ghost, session.elapsedMs);
    if (pose) {
      ghostSpeed = pose.speed;
      ghostPoseFrame = { x: pose.x, y: pose.y, heading: pose.heading };
    }
  }

  const rivals: WorldFrame['rivals'] = aiRacers.map((r) => ({
    x: r.session.kart.x,
    y: r.session.kart.y,
    heading: r.session.kart.heading,
    steer: r.session.kart.steer,
    livery: r.livery,
  }));

  renderer.draw({
    kart: { x, y, heading, steer: k.steer, livery: liveryById(settings.livery), latG: session.liveLatG, damage: session.damage },
    ghost: ghostPoseFrame,
    rivals,
    effects,
    trail: null,
    referenceLine: settings.referenceLine,
    speedFx,
  });

  drillHud.hidden = session.drill === null;
  drawDrillOverlay();

  hud.draw(
    hudCtx!,
    renderer.viewport,
    {
      lapMs: session.elapsedMs,
      speedKph: speed * 3.6,
      bestMs: session.drill ? null : day.bestMs,
      idealMs: session.drill
        ? (session.drill.idealMs ?? 0)
        : world.profile.idealLapTime * 1000,
      deltaMs: running ? session.deltaTo(ghost) : null,
      brake: session.liveControls.brake,
      locked: k.locked,
      offTrack: !session.onTrack,
      progress: session.progress,
      // A drill is not a lap: sector boxes, the medal target and the lap best
      // all refer to something that is not happening, so they are suppressed
      // rather than left on screen showing stale numbers.
      sectors: session.drill ? [] : (session.result?.sectorMs ?? session.sectorMs),
      bestSectors: session.drill ? null : bestSectors,
      tyre: settings.tyreModel
        ? { temp: session.tyres.temp, wear: session.tyres.wear, label: tyreLabel(session.tyres) }
        : null,
      weather: world.weather.name,
      wet: world.weather.id !== 'dry',
      practice: session.practice,
      speedDeltaKph: running && ghostSpeed !== null ? (speed - ghostSpeed) * 3.6 : null,
      medalTarget: session.drill ? null : medalTargetFor(session.elapsedMs, running),
      respawns: session.respawns,
      damage: session.damage,
      race: racePosition(),
      drafting: aiRacers.length > 0 && running && isDrafting(),
      latG: session.liveLatG,
      longG: session.liveLongG,
      rpm: IDLE_RPM + (REDLINE_RPM - IDLE_RPM) * clamp(speed / CAR_TOP_SPEED, 0, 1),
    },
    x,
    y,
  );

  drawGpStartLights();
}

/** Whether the player is currently in another kart's slipstream, for the HUD badge only. */
function isDrafting(): boolean {
  const scales = computeSlipstream([session.kart, ...aiRacers.map((r) => r.session.kart)]);
  return scales[0] < 0.99;
}

/**
 * Player's live rank among the AI grid — finished karts always outrank one
 * still on track, since in a single-lap race they have unambiguously crossed
 * the line first. Null outside Grand Prix.
 */
function racePosition(): { position: number; total: number } | null {
  if (aiRacers.length === 0) return null;
  const mine = raceProgress(session);
  let ahead = 0;
  for (const r of aiRacers) if (raceProgress(r.session) > mine) ahead++;
  return { position: ahead + 1, total: aiRacers.length + 1 };
}

/** A single comparable "how far through the race" number for HUD ranking only. */
function raceProgress(s: Session): number {
  if (s.phase === 'finished' && s.result) return 1e9 - s.result.timeMs;
  return wrapIndex(s.currentIndex - world.track.startIndex, world.track.center.length);
}

/**
 * What to chase right now: while running, the medal your *current pace* is short
 * of; when idle, the one above your best.
 */
function medalTargetFor(
  elapsedMs: number,
  running: boolean,
): { name: string; color: string; gapMs: number } | null {
  const idealMs = world.profile.idealLapTime * 1000;
  const reference = running ? day.bestMs ?? null : day.bestMs;
  if (reference === null && !running) return null;

  const basis = running ? reference ?? Infinity : reference!;
  if (!Number.isFinite(basis)) return null;

  const next = nextMedal(basis, idealMs);
  if (!next) return null;
  void elapsedMs;
  return { name: next.medal.name, color: next.medal.color, gapMs: next.gapMs };
}

/**
 * Countdown and attempt-result flash, drawn over the track.
 *
 * Both live on the canvas rather than in the DOM so they sit in the driver's
 * eyeline instead of at the edge of the screen, and so the flash can fade out
 * without a reflow between attempts.
 */
function drawDrillOverlay(): void {
  const c = hudCtx!;
  const v = renderer.viewport;
  const w = v.viewW;
  const h = v.viewH;
  c.save();
  c.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
  c.textAlign = 'center';
  c.textBaseline = 'middle';

  if (session.drill && session.holdTicks > 0) {
    const seconds = session.holdTicks / TICK_HZ;
    const count = Math.ceil(seconds);
    // Each digit swells as it lands, so the release moment is unmistakable.
    const frac = 1 - (seconds - Math.floor(seconds));
    c.globalAlpha = 0.35 + frac * 0.55;
    c.fillStyle = '#f2f4f8';
    c.font = `700 ${Math.round(64 + frac * 18)}px ui-monospace, Menlo, monospace`;
    c.fillText(String(count), w / 2, h * 0.4);
    c.globalAlpha = 1;
  }

  if (drillFlash) {
    const left = drillFlash.until - performance.now();
    if (left <= 0) {
      drillFlash = null;
    } else {
      c.globalAlpha = Math.min(1, left / 320);
      c.fillStyle = drillFlash.good ? '#b06bff' : '#f2f4f8';
      c.font = '700 52px ui-monospace, Menlo, monospace';
      c.fillText(drillFlash.text, w / 2, h * 0.28);
      c.fillStyle = drillFlash.good ? '#d9bcff' : '#9aa4b2';
      c.font = '600 17px system-ui, sans-serif';
      c.fillText(drillFlash.sub, w / 2, h * 0.28 + 38);
      c.globalAlpha = 1;
    }
  }

  c.restore();
}

/**
 * Grand Prix start lights: five reds illuminate in sequence, hold, then all
 * go out together — the same shape as a real formation start. The kart is
 * physically frozen for the whole sequence (`session.holdTicks`, see
 * `Session.launch`), so this is purely a read of state that already exists.
 */
function drawGpStartLights(): void {
  const inHold = aiRacers.length > 0 && session.phase === 'running' && session.holdTicks > 0;

  if (wasInGpHold && !inHold && aiRacers.length > 0) {
    gpGoFlashUntil = performance.now() + 700;
  }
  wasInGpHold = inHold;

  const c = hudCtx!;
  const v = renderer.viewport;
  const w = v.viewW;
  const h = v.viewH;
  const lightsY = h * 0.22;

  if (inHold) {
    const remaining = session.holdTicks / TICK_HZ;
    const elapsed = gpHoldSeconds - remaining;
    const litCount =
      elapsed >= GP_LIGHTS_ON_DURATION
        ? GP_LIGHT_COUNT
        : Math.min(GP_LIGHT_COUNT, Math.floor(elapsed / GP_LIGHT_INTERVAL) + 1);

    c.save();
    c.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    const size = 20;
    const gap = 14;
    const totalW = GP_LIGHT_COUNT * size + (GP_LIGHT_COUNT - 1) * gap;
    const startX = w / 2 - totalW / 2;

    c.fillStyle = 'rgba(8,10,13,0.6)';
    c.fillRect(startX - 16, lightsY - 16, totalW + 32, size + 32);

    for (let i = 0; i < GP_LIGHT_COUNT; i++) {
      const cx = startX + i * (size + gap) + size / 2;
      const cy = lightsY + size / 2;
      c.beginPath();
      c.arc(cx, cy, size / 2, 0, Math.PI * 2);
      c.fillStyle = i < litCount ? '#ff3b30' : '#3a1512';
      c.fill();
    }
    c.restore();
  }

  const left = gpGoFlashUntil - performance.now();
  if (left > 0) {
    c.save();
    c.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    c.globalAlpha = Math.min(1, left / 300);
    c.fillStyle = '#3ecf7a';
    c.font = '700 56px ui-monospace, Menlo, monospace';
    c.fillText('GO!', w / 2, lightsY + 24);
    c.globalAlpha = 1;
    c.restore();
  }
}

let lastCuedCorner = -1;

/**
 * Beeps as you reach each corner's ideal braking point.
 *
 * The marker boards already show where to brake, but only if you are looking at
 * the right side of the road at the right moment. A sound arrives whether or not
 * you were looking, which is the point.
 */
function maybeBrakeCue(): void {
  if (!settings.brakeCue || !settings.sound) return;
  const idx = session.currentIndex;
  for (const corner of world.corners) {
    if (corner.number === lastCuedCorner) continue;
    const n = world.track.center.length;
    const distance = (idx - corner.brakeIndex + n) % n;
    // Within a couple of samples of the braking point, approaching it.
    if (distance <= 2) {
      audio.blip(520, 0.09);
      lastCuedCorner = corner.number;
      return;
    }
  }
}

function renderReplay(frameDt: number): void {
  const r = replay;
  if (!r) return;
  if (r.playing) {
    r.timeMs += frameDt * 1000 * r.rate;
    if (r.timeMs >= r.ghost.timeMs) {
      r.timeMs = r.ghost.timeMs;
      r.playing = false;
      updatePlayButton(false);
    }
  }

  const pose = ghostPose(r.ghost, r.timeMs);
  if (!pose) return;

  renderer.follow(pose.x, pose.y, pose.heading, pose.speed, frameDt);
  renderer.draw({
    kart: {
      x: pose.x,
      y: pose.y,
      heading: pose.heading,
      steer: r.ghost.steer[0] ?? 0,
      livery: liveryById(settings.livery),
    },
    ghost: null,
    rivals: [],
    effects: null,
    trail: {
      x: r.ghost.x,
      y: r.ghost.y,
      speed: r.ghost.speed,
      from: Math.max(0, clamp(Math.floor(frameOf(r)), 0, r.ghost.x.length - 1) - 140),
      to: clamp(Math.floor(frameOf(r)), 0, r.ghost.x.length - 1),
    },
    referenceLine: settings.referenceLine,
  });

  $<HTMLInputElement>('replay-scrub').value = String(
    Math.round((r.timeMs / Math.max(1, r.ghost.timeMs)) * 1000),
  );
  $('replay-time').textContent = formatTime(r.timeMs);
  if (r.playing) drawTelemetry();
}

// ------------------------------------------------------------------- boot

function tickCountdown(): void {
  const ms = msUntilNextTrack();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  $('countdown').textContent =
    `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

bestGhost = decodeBest();
bestSectors = day.bestSectors ?? null;
renderer.setPalette(settings.colorblind);
renderer.snap(session.kart.x, session.kart.y, session.kart.heading);

applyTextScale();
applyAccessibilityFlags();
populateRace();
buildPickers();
applyMode(settings.realism);
bindOptions();
bindTabs();
bindCircuit();
bindReplay();
renderAttemptBars();
tickCountdown();
setInterval(tickCountdown, 1000);
showStart();

// A player who left the game in 3D picks it back up in 3D, but the boot
// itself stays on the synchronous 2D path so a slow Three.js fetch never
// delays first paint.
if (settings.renderer === '3d') void switchRenderer('3d');

audio.setEnabled(settings.sound);
audio.setVolume(settings.volume);

input.attach(document.body);
input.setCallbacks(
  () => {
    if (replay || drillCorner) return;
    if (session.phase !== 'running' && startPanel.hidden === false) beginRun();
    else if (session.phase === 'finished') beginRun();
  },
  // R retries the drill rather than dumping you into a full standing-start lap,
  // which is what it used to do and what made the mode a dead end.
  () => (drillCorner ? restartDrill() : beginRun()),
);

$('start-button').addEventListener('click', beginRun);
// The summary line names the realism mode and the team, so it opens the group
// that holds them rather than dropping you on five collapsed headers.
$('setup-summary').addEventListener('click', () => {
  selectTab('settings');
  $<HTMLDetailsElement>('settings-driving').open = true;
});
// The leaderboard names the player inline so renaming is one click away
// from the place that motivates it, rather than a buried Options field.
$('leaderboard-rename').addEventListener('click', () => {
  showStart();
  selectTab('settings');
  $<HTMLDetailsElement>('settings-account').open = true;
  $<HTMLInputElement>('opt-name').focus();
});
// Without this the result panel was a dead end: no way back to settings, the
// circuit list or stats short of reloading the page.
$('result-menu').addEventListener('click', showStart);
// retry-button's handler is (re)assigned in renderGrandPrixResultBanner on every
// result — outside a Grand Prix it defaults to beginRun, same as always.
$('practice-button').addEventListener('click', () => {
  buildPracticeList();
  hidePanels();
  practicePanel.hidden = false;
});
$('practice-back').addEventListener('click', showStart);
$('grandprix-button').addEventListener('click', () => {
  renderGrandPrixIntro();
  hidePanels();
  grandprixPanel.hidden = false;
});
$('grandprix-back').addEventListener('click', showStart);
$('grandprix-start-button').addEventListener('click', beginGrandPrix);
$('grandprix-room-create').addEventListener('click', async () => {
  const result = await createRoom();
  if (result) {
    setRoomCode(result.code);
    renderRoomJoinOrStatus();
  }
});
$('grandprix-room-join-button').addEventListener('click', async () => {
  const input = $<HTMLInputElement>('grandprix-room-code-input');
  const code = input.value.trim().toUpperCase();
  if (!code) return;
  const result = await joinRoom(code);
  if (result && result.status === 'ok') {
    setRoomCode(result.code);
    input.value = '';
    renderRoomJoinOrStatus();
  } else {
    flash($('grandprix-room-join-button'), result?.message ?? 'Could not join');
  }
});
$('grandprix-room-copy').addEventListener('click', async () => {
  const code = roomCode();
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
    flash($('grandprix-room-copy'), 'Copied');
  } catch {
    /* clipboard blocked */
  }
});
$('grandprix-room-leave').addEventListener('click', () => {
  clearRoomCode();
  renderRoomJoinOrStatus();
});
$('gp-exit-button').addEventListener('click', exitGrandPrix);
$('gp-result-exit').addEventListener('click', exitGrandPrix);
$('quali-run').addEventListener('click', runQualiLap);
$('quali-done').addEventListener('click', doneWithSegment);
$('quali-exit').addEventListener('click', exitGrandPrix);
$('gp-result-done').addEventListener('click', exitGrandPrix);
$('gp-result-again').addEventListener('click', beginGrandPrix);
$('drill-exit').addEventListener('click', exitDrill);
$('drill-approach').addEventListener('click', () => {
  drillMode = drillMode === 'approach' ? 'corner' : 'approach';
  settings = updateSettings({ drillApproach: drillMode === 'approach' });
  // Changing the segment invalidates the target and the bests it was set against.
  drillGhost = null;
  session.reset();
  restartDrill();
});
$('replay-button').addEventListener('click', () => {
  if (session.result) enterReplay(session.result.ghost);
});
$('share-button').addEventListener('click', () => void openShare());
$('share-close').addEventListener('click', () => {
  hidePanels();
  resultPanel.hidden = false;
});

$('copy-button').addEventListener('click', async () => {
  const r = session.result;
  if (!r) return;
  const text = buildShareText(world.label, r, {
    bestSplits: day.bestSplits,
    objectives: lastObjectiveResults,
    mode: realismById(settings.realism).name,
    weather: world.weather.name,
  });
  try {
    await navigator.clipboard.writeText(text);
    flash($('copy-button'), 'Copied');
  } catch {
    /* clipboard blocked */
  }
});

window.addEventListener('keydown', (e) => {
  if (e.code === 'Escape') {
    if (replay) exitReplay();
    // A drill hides every panel, so without this branch Escape did nothing at
    // all and there was no way out of the mode short of reloading.
    else if (drillCorner) exitDrill();
    // Escape means "up one level" from every panel, without exception — see
    // docs/menu-plan.md. Any new panel belongs in this list.
    else if (!grandprixResultPanel.hidden) exitGrandPrix();
    else if (!qualiPanel.hidden) exitGrandPrix();
    // A qualifying lap's result panel goes back to the timing board, not the
    // pre-race screen.
    else if (quali && !resultPanel.hidden) backToTiming();
    else if (
      !sharePanel.hidden ||
      !practicePanel.hidden ||
      !grandprixPanel.hidden ||
      !resultPanel.hidden
    ) {
      showStart();
    }
    // Stats, Settings and — on a narrow viewport — the Circuit and Garage
    // columns are one level below the menu's Race column, so Escape goes there
    // rather than nowhere (P4).
    else if (!startPanel.hidden && currentTab !== 'race') selectTab('race');
    // A running lap hides every panel too, so Escape fell through this chain
    // and the only exit was to finish the lap — a dead end (P4). Abandoning it
    // goes up one level to the start screen, one input from driving again.
    //
    // A qualifying lap goes up to its own timing board rather than the start
    // screen — that is the level above it. It needs its own branch precisely
    // because `beginRun` hides `qualiPanel` as well: with qualifying merely
    // excluded here, a running quali lap matched no branch at all and Escape
    // did nothing, which is the dead end P4 forbids.
    else if (session.phase === 'running' && quali && startPanel.hidden) {
      session.reset();
      backToTiming();
    } else if (session.phase === 'running' && !quali && startPanel.hidden) {
      session.reset();
      showStart();
    }
    return;
  }
  if (e.code === 'Backspace' && !replay && session.phase === 'running') {
    e.preventDefault();
    // In a drill, respawn *is* retry — both put you back on the approach.
    if (drillCorner) {
      restartDrill();
      return;
    }
    // On a lap, respawn goes to the last checkpoint. The clock keeps running,
    // so it is always a loss on a good lap and a rescue on a ruined one.
    session.respawn();
    lastCuedCorner = -1;
    prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
    renderer.snap(session.kart.x, session.kart.y, session.kart.heading);
    effects.clear();
  }
});

void refreshLeaderboard();

if (import.meta.env.DEV) {
  // Rebuilt on every call rather than captured once, so it tracks whichever
  // circuit is currently loaded instead of the one present at page load.
  const pilot = (): Autopilot =>
    new Autopilot(world.track, solveSpeedProfile(world.track, world.weather.muAt, 0), session.query);
  // Drives whatever the session is currently doing with the reference driver —
  // works for a lap or a drill, so the drill loop can be exercised headlessly.
  (window as unknown as { autoDrive: (n?: number) => unknown }).autoDrive = (
    maxTicks = TICK_HZ * 30,
  ) => {
    let completions = 0;
    const p = pilot();
    for (let i = 0; i < maxTicks; i++) {
      if (session.phase !== 'running') break;
      const { steerByte, brakeByte } = p.sample(session.kart);
      prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
      if (session.tick(steerByte, brakeByte)) {
        completions++;
        if (session.drill) onDrillComplete();
        else showResult();
      }
    }
    return {
      completions,
      phase: session.phase,
      drill: session.drill?.label ?? null,
      attempts: session.drillAttempts,
      best: session.drillBestMs,
      last: session.drillResult?.timeMs ?? null,
      ideal: session.drill?.idealMs ?? null,
      holdTicks: session.holdTicks,
    };
  };

  // Holds a fixed steering input for a while. The only reliable way to answer
  // "does pressing right actually go right on screen" — the class of bug a
  // mirrored world coordinate mapping produces.
  (window as unknown as { steerTest: (s: number, n?: number) => unknown }).steerTest = (
    steer,
    ticks = TICK_HZ,
  ) => {
    const before = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
    const byte = Math.round(clamp(steer, -1, 1) * 127);
    for (let i = 0; i < ticks && session.phase === 'running'; i++) {
      prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
      session.tick(byte, 0);
    }
    // Positive cross product = the kart moved to the LEFT of where it pointed.
    const fx = Math.cos(before.heading);
    const fy = Math.sin(before.heading);
    const dx = session.kart.x - before.x;
    const dy = session.kart.y - before.y;
    return {
      steerInput: steer,
      headingDelta: session.kart.heading - before.heading,
      lateralOffset: fx * dy - fy * dx,
    };
  };

  (window as unknown as { fastLap: (n?: number) => number | null }).fastLap = (
    maxTicks = TICK_HZ * 120,
  ) => {
    beginRun();
    const p = pilot();
    for (let i = 0; i < maxTicks && session.phase === 'running'; i++) {
      const { steerByte, brakeByte } = p.sample(session.kart);
      prev = { x: session.kart.x, y: session.kart.y, heading: session.kart.heading };
      let playerDrag = 1;
      let aiDrag: number[] | undefined;
      let field: KartState[] | undefined;
      if (aiRacers.length > 0) {
        field = [session.kart, ...aiRacers.map((r) => r.session.kart)];
        const scales = computeSlipstream(field);
        playerDrag = scales[0];
        aiDrag = scales.slice(1);
      }
      const finished = session.tick(steerByte, brakeByte, playerDrag);
      if (field) {
        tickAIGrid(aiRacers, aiDrag, field);
        resolveKartCollisions(field);
      }
      if (finished) showResult();
    }
    return session.result?.timeMs ?? null;
  };
}

new GameLoop(tick, render).start();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('./sw.js');
  });
}
